import * as z10 from 'zod';
import { z } from 'zod';
import { createId } from '@paralleldrive/cuid2';
import Decimal from 'decimal.js';

// src/aria/AriaQuestion.ts
var AriaQuestionSchema = z.object({
  id: z.string().describe("a unique id for the question"),
  question: z.string().describe("the question being asked"),
  answer: z.string().describe("the answer to the question will always be provided by the user, not the AI")
});

// src/enums/MoscowPriority.ts
var MoscowPriority = /* @__PURE__ */ ((MoscowPriority2) => {
  MoscowPriority2["Must_Have"] = "Must Have";
  MoscowPriority2["Should_Have"] = "Should Have";
  MoscowPriority2["Could_Have"] = "Could Have";
  MoscowPriority2["Will_Not_Have"] = "Will Not Have";
  return MoscowPriority2;
})(MoscowPriority || {});

// src/endpoints/useEndpoint.ts
var LOCAL_BANDROOM_PORT = 9999;
var LOCAL_COGNOS_PORT = 3141;
var LOCAL_PROXY_PORT = 8444;
var PROXY_SHAPE_PATH = "/v1/shape";
var LOCALTEST_DOMAIN = "localtest.me";
var PROD_BASE_DOMAIN = "gorhythmic.com";
var EMPTY_ENDPOINTS = {
  bandroomEndpoint: void 0,
  cognosEndpoint: void 0,
  reactiveDbShapeUrl: void 0
};
function buildUrl(scheme, host, port, path) {
  const normalizedHost = host.trim();
  const portPart = typeof port === "number" || typeof port === "string" && port.trim().length > 0 ? `:${String(port).trim()}` : "";
  const normalizedPath = path && path.trim().length > 0 ? path.trim().startsWith("/") ? path.trim() : `/${path.trim()}` : "";
  return `${scheme}://${normalizedHost}${portPart}${normalizedPath}`;
}
function getDefaultHostInput() {
  const globalRecord = globalThis;
  const location = globalRecord.location;
  if (!location) return void 0;
  if (typeof location.href === "string" && location.href.length > 0) return location.href;
  if (typeof location.host === "string" && location.host.length > 0) return location.host;
  return void 0;
}
function parseHostLike(input) {
  const trimmed = input.trim();
  if (!trimmed) return void 0;
  const candidate = trimmed.includes("://") ? trimmed : `http://${trimmed}`;
  try {
    const url2 = new URL(candidate);
    const hostname = url2.hostname.toLowerCase();
    const port = url2.port || void 0;
    return { hostname, port };
  } catch {
    return void 0;
  }
}
function logInferenceWarning(message, details) {
  if (typeof console === "undefined" || typeof console.warn !== "function") return;
  if (details) console.warn(`[useEndpoint] ${message}`, details);
  else console.warn(`[useEndpoint] ${message}`);
}
function isLocalHost(hostname) {
  if (!hostname) return false;
  if (hostname === "localhost") return true;
  if (hostname === "127.0.0.1") return true;
  if (hostname === "::1") return true;
  return false;
}
function isLocaltestHost(hostname) {
  return hostname.endsWith(`.${LOCALTEST_DOMAIN}`);
}
function isGorhythmicHost(hostname) {
  if (hostname === PROD_BASE_DOMAIN) return true;
  if (hostname === `www.${PROD_BASE_DOMAIN}`) return true;
  return hostname.endsWith(`.${PROD_BASE_DOMAIN}`);
}
function resolveLocalEndpoints() {
  return {
    bandroomEndpoint: buildUrl("https", "bandroom.localtest.me", LOCAL_BANDROOM_PORT),
    cognosEndpoint: buildUrl("http", "localhost", LOCAL_COGNOS_PORT),
    reactiveDbShapeUrl: buildUrl("https", "proxy.localtest.me", LOCAL_PROXY_PORT, PROXY_SHAPE_PATH)
  };
}
function resolveGorhythmicEndpoints(hostname) {
  const suffix = `.${PROD_BASE_DOMAIN}`;
  const subdomain = hostname.endsWith(suffix) ? hostname.slice(0, hostname.length - suffix.length) : "";
  const segments = subdomain.split(".").filter(Boolean);
  const serviceNames = /* @__PURE__ */ new Set(["bandroom", "cognos"]);
  const ignoredSegments = /* @__PURE__ */ new Set(["www", "app"]);
  const enterpriseSlug = segments.find((segment) => !ignoredSegments.has(segment) && !serviceNames.has(segment));
  function buildServiceHost(service) {
    const host = enterpriseSlug ? `${enterpriseSlug}.${service}.${PROD_BASE_DOMAIN}` : `${service}.${PROD_BASE_DOMAIN}`;
    return buildUrl("https", host);
  }
  const bandroomEndpoint = buildServiceHost("bandroom");
  const cognosEndpoint = buildServiceHost("cognos");
  const proxyHost = enterpriseSlug ? `${enterpriseSlug}.proxy.${PROD_BASE_DOMAIN}` : `proxy.${PROD_BASE_DOMAIN}`;
  const reactiveDbShapeUrl = buildUrl("https", proxyHost, void 0, PROXY_SHAPE_PATH);
  return {
    bandroomEndpoint,
    cognosEndpoint,
    reactiveDbShapeUrl
  };
}
function resolveEndpointsForHost(rawHost) {
  const hostInput = rawHost && rawHost.trim().length > 0 ? rawHost : getDefaultHostInput();
  if (!hostInput) {
    logInferenceWarning("No host available for endpoint resolution");
    return EMPTY_ENDPOINTS;
  }
  const hostParts = parseHostLike(hostInput);
  if (!hostParts) {
    logInferenceWarning("Unable to parse host for endpoint resolution", {
      host: hostInput
    });
    return EMPTY_ENDPOINTS;
  }
  const { hostname } = hostParts;
  if (isLocalHost(hostname) || isLocaltestHost(hostname)) return resolveLocalEndpoints();
  if (isGorhythmicHost(hostname)) return resolveGorhythmicEndpoints(hostname);
  logInferenceWarning("Unable to infer endpoints from host; returning undefined endpoints", { hostname });
  return EMPTY_ENDPOINTS;
}
function useEndpoint(rawHost) {
  return resolveEndpointsForHost(rawHost);
}

// src/forecast/forecastType.ts
var ForecastType = /* @__PURE__ */ ((ForecastType2) => {
  ForecastType2["Iteration"] = "iteration";
  ForecastType2["Release"] = "release";
  return ForecastType2;
})(ForecastType || {});
var scalarValueSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]).meta({
  title: "Board Integration Scalar Value",
  description: "Primitive values accepted in mapping definitions for literal assignments and fallbacks.",
  examples: ["Doing", 42, true, null]
});
var conflictResolutionModes = ["overwrite-provider", "overwrite-rhythmic", "most-recent", "fail"];
var BoardIntegrationConflictResolutionModeSchema = z.enum(conflictResolutionModes).meta({
  title: "Board Integration Conflict Resolution Mode",
  description: "Controls how conflicting data is resolved between Rhythmic and the external provider.",
  examples: ["most-recent"]
});
var simplifiedConflictResolutionModes = ["take-provider", "take-rhythmic", "nochange"];
var conflictResolutionAliasMap = {
  "take-provider": "overwrite-provider",
  "take-rhythmic": "overwrite-rhythmic",
  nochange: "overwrite-rhythmic"
};
function isSimplifiedConflictMode(value) {
  return typeof value === "string" && simplifiedConflictResolutionModes.includes(value);
}
var SimplifiedConflictResolutionModeSchema = z.enum(simplifiedConflictResolutionModes).meta({
  title: "Simplified Conflict Resolution Mode",
  description: "Legacy-friendly aliases mapped into canonical conflict resolution modes.",
  examples: ["take-provider"]
});
var NormalizedConflictResolutionModeSchema = z.union([BoardIntegrationConflictResolutionModeSchema, SimplifiedConflictResolutionModeSchema]).transform((mode) => isSimplifiedConflictMode(mode) ? conflictResolutionAliasMap[mode] : mode).meta({
  title: "Normalized Conflict Resolution Mode",
  description: "Accepts canonical or simplified inputs and normalizes them into the canonical mode string.",
  examples: ["overwrite-provider"]
});
var BoardIntegrationProviderKeySchema = z.enum(["azure-devops", "jira", "github"]).meta({
  title: "Board Integration Provider Key",
  description: "Supported provider identifiers for board integrations.",
  examples: ["azure-devops", "jira", "github"]
});
var BoardIntegrationRuleSchema = z.enum(["status", "type", "field", "iteration", "custom"]).meta({
  title: "Board Integration Rule",
  description: "Rule categories used to build mappings between Rhythmic and provider artifacts.",
  examples: ["status"]
});
var BoardIntegrationDirectionSchema = z.enum(["push", "pull", "sync"]).meta({
  title: "Board Integration Sync Direction",
  description: "Defines whether mappings push to the provider, pull from it, or stay in sync.",
  examples: ["sync"]
});
var BoardValueScopeSchema = z.enum(["board", "column", "card"]).meta({
  title: "Board Value Scope",
  description: "Granularity level for board values referenced by mappings.",
  examples: ["column"]
});
var BoardValueFieldSchema = z.enum(["status", "workitemType", "iteration_id", "context", "cost_per_iteration", "custom"]).meta({
  title: "Board Value Field",
  description: "Board-level fields that can be controlled through integration mappings.",
  examples: ["status"]
});
var WorkitemStatusKeySchema = z.enum(["Not_Ready", "Ready", "Doing", "Done", "Removed"]).meta({
  title: "Workitem Status Key",
  description: "Internal normalized workitem statuses used by Rhythmic.",
  examples: ["Doing"]
});
var BoardIntegrationMapProviderRefSchema = z.object({
  integrationId: z.string().min(1, "integrationId is required to reference a team integration").meta({
    title: "Integration ID",
    description: "Rhythmic integration identifier that points to the provider connection.",
    examples: ["int_123"]
  }),
  key: BoardIntegrationProviderKeySchema.meta({
    title: "Provider Key",
    description: "Identifies which provider applies to this mapping entry.",
    examples: ["azure-devops"]
  }),
  providerMetadataId: z.string().min(1).optional().meta({
    title: "Provider Metadata ID",
    description: "Optional provider-specific metadata identifier used for advanced lookups.",
    examples: ["meta_456"]
  }),
  project: z.string().min(1).optional().meta({
    title: "Provider Project",
    description: "Optional provider project or collection context.",
    examples: ["Fabrication"]
  }),
  workspaceId: z.string().min(1).optional().meta({
    title: "Workspace ID",
    description: "Workspace identifier used when the mapping references a specific workspace.",
    examples: ["ws_123"]
  })
}).strict().meta({
  title: "Board Integration Provider Reference",
  description: "Reference data that ties a mapping entry to a Rhythmic integration instance."
});
var BoardIntegrationMapTransformSchema = z.object({
  kind: z.enum(["identity", "lookup", "expression"]).meta({
    title: "Transform Kind",
    description: "Type of transformation applied to provider data before syncing.",
    examples: ["lookup"]
  }),
  expression: z.string().min(1).optional().meta({
    title: "Transform Expression",
    description: "Expression body used for calculated transforms.",
    examples: ['priority === "high" ? "P1" : "P2"']
  }),
  fallback: scalarValueSchema.optional().meta({
    title: "Fallback Value",
    description: "Value returned when a transform cannot derive a value.",
    examples: ["Unknown"]
  })
}).strict().meta({
  title: "Board Integration Transform",
  description: "Describes optional value transformations for provider values."
});
var BoardIntegrationMapProviderMappingRefSchema = z.object({
  type: z.enum(["statusMappings", "typeMappings", "custom"]).meta({
    title: "Mapping Reference Type",
    description: "Category of mapping referenced when using shared mapping tables.",
    examples: ["statusMappings"]
  }),
  key: z.string().min(1).meta({
    title: "Mapping Reference Key",
    description: "Unique identifier used to locate a mapping inside the referenced type.",
    examples: ["statusDoing"]
  }),
  path: z.string().min(1).optional().meta({
    title: "Mapping Reference Path",
    description: "Optional JSON path used to drill into nested mapping definitions.",
    examples: ["children.0"]
  })
}).strict().meta({
  title: "Board Integration Mapping Reference",
  description: "Reference to reusable provider mappings stored elsewhere."
});
var BoardIntegrationMapProviderValueSchema = z.object({
  entity: z.enum(["workItem", "issue", "board"]).meta({
    title: "Provider Entity",
    description: "Provider entity type that holds the value.",
    examples: ["workItem"]
  }),
  field: z.string().min(1).meta({
    title: "Provider Field",
    description: "Field on the provider entity that this mapping targets.",
    examples: ["System.State"]
  }),
  value: scalarValueSchema.optional().meta({
    title: "Literal Provider Value",
    description: "Literal value assigned to the provider field when mapping.",
    examples: ["Closed"]
  }),
  mappingRef: BoardIntegrationMapProviderMappingRefSchema.optional().meta({
    title: "Provider Mapping Reference",
    description: "Shared mapping reference used instead of a literal provider value."
  }),
  transform: BoardIntegrationMapTransformSchema.optional().meta({
    title: "Provider Value Transform",
    description: "Transform instructions applied to build the provider value dynamically."
  })
}).strict().superRefine((value, ctx) => {
  if (value.value === void 0 && !value.mappingRef && !value.transform) {
    ctx.addIssue({
      code: "custom",
      message: "providerValue must specify a literal value, mappingRef, or transform",
      path: ["value"]
    });
  }
}).meta({
  title: "Board Integration Provider Value",
  description: "Describes how provider-side values are sourced for a mapping entry."
});
var BoardIntegrationFieldMappingTargetSchema = z.enum(["custom", "iteration", "workitemType", "status"]).meta({
  title: "Field Mapping Target",
  description: "Destination target for Rhythmic mappings.",
  examples: ["status"]
});
var BoardIntegrationFieldMappingModeSchema = z.enum(["field", "fixed"]).meta({
  title: "Field Mapping Mode",
  description: "Determines whether a mapping reads from a source field or uses a fixed value.",
  examples: ["field"]
});
var BoardIntegrationFieldValueMappingSchema = z.object({
  id: z.string().min(1).meta({
    title: "Value Mapping ID",
    description: "Unique identifier for the value mapping entry.",
    examples: ["vm_abc"]
  }),
  remoteValue: z.string().min(1).meta({
    title: "Remote Value",
    description: "Value observed from the provider that should be translated.",
    examples: ["To Do"]
  }),
  targetValue: z.string().optional().meta({
    title: "Target Value",
    description: "Rhythmic value that remoteValue should translate into.",
    examples: ["Not Ready"]
  }),
  iterationId: z.string().optional().meta({
    title: "Iteration ID",
    description: "Target iteration identifier associated with the mapping.",
    examples: ["itr_001"]
  }),
  boardId: z.string().optional().meta({
    title: "Board ID",
    description: "Board identifier impacted by this mapping.",
    examples: ["board_1"]
  }),
  columnId: z.string().optional().meta({
    title: "Column ID",
    description: "Board column identifier when the mapping targets a column.",
    examples: ["col_2"]
  }),
  derivedStatus: z.string().optional().meta({
    title: "Derived Status",
    description: "Status automatically computed when remote values map to Rhythmic statuses.",
    examples: ["Ready"]
  }),
  config: z.record(z.string(), z.unknown()).optional().meta({
    title: "Value Mapping Config",
    description: "Key/value settings that further customize the value mapping."
  })
}).strict().meta({
  title: "Board Integration Field Value Mapping",
  description: "Translates remote provider values to Rhythmic targets."
});
var workitemFieldSchema = z.string().optional().nullable().transform((value) => {
  if (typeof value !== "string") return void 0;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : void 0;
}).meta({
  title: "Workitem Field",
  description: "Optional provider workitem field name trimmed to eliminate blank values.",
  examples: ["Custom.Priority"]
});
var BoardIntegrationFieldMappingSchema = z.object({
  id: z.string().min(1).meta({
    title: "Field Mapping ID",
    description: "Unique identifier for the field mapping configuration.",
    examples: ["fm_123"]
  }),
  fieldKey: z.string().min(1).meta({
    title: "Field Key",
    description: "Key referencing the Rhythmic field targeted by this mapping.",
    examples: ["status"]
  }),
  displayName: z.string().min(1).optional().meta({
    title: "Display Name",
    description: "Human friendly label displayed in UI contexts.",
    examples: ["Status Mapping"]
  }),
  target: BoardIntegrationFieldMappingTargetSchema.default("custom").meta({
    title: "Field Target",
    description: "Destination entity that receives the mapped value."
  }),
  destinationField: z.string().min(1).optional().meta({
    title: "Destination Field",
    description: "Specific Rhythmic field for mappings that target a custom destination.",
    examples: ["customFields.priority"]
  }),
  workitemField: workitemFieldSchema.meta({
    title: "Provider Workitem Field",
    description: "Source field name on the provider work item.",
    examples: ["System.State"]
  }),
  mode: BoardIntegrationFieldMappingModeSchema.default("field").meta({
    title: "Field Mapping Mode",
    description: "Indicates whether the mapping uses a provider field or a fixed value."
  }),
  fixedValue: z.string().optional().nullable().meta({
    title: "Fixed Value",
    description: "Fixed literal applied when the mapping mode is set to fixed.",
    examples: ["Blocked"]
  }),
  valueMappings: z.array(BoardIntegrationFieldValueMappingSchema).default([]).meta({
    title: "Value Mappings",
    description: "Collection of value translation entries applied to the mapping."
  }),
  conflictResolutionMode: NormalizedConflictResolutionModeSchema.default("overwrite-provider").meta({
    title: "Field Conflict Resolution Mode",
    description: "Overrides the default conflict handling strategy for this field mapping."
  }),
  config: z.record(z.string(), z.unknown()).optional().meta({
    title: "Field Mapping Config",
    description: "Key/value options that tailor mapping behavior at runtime."
  })
}).strict().meta({
  title: "Board Integration Field Mapping",
  description: "Configuration describing how a single field is synchronized between systems."
});
var BoardIntegrationMapBoardValueSchema = z.object({
  scope: BoardValueScopeSchema.meta({
    title: "Board Value Scope",
    description: "Scope indicating whether the mapping targets the entire board, a column, or a card."
  }),
  field: BoardValueFieldSchema.meta({
    title: "Board Field",
    description: "Board field name controlled by the mapping."
  }),
  status: WorkitemStatusKeySchema.optional().meta({
    title: "Rhythmic Status",
    description: "Normalized Rhythmic status used when field equals status.",
    examples: ["Ready"]
  }),
  columnId: z.string().min(1).optional().meta({
    title: "Column ID",
    description: "Target board column identifier when scope is set to column.",
    examples: ["col_4"]
  }),
  value: scalarValueSchema.optional().meta({
    title: "Board Value",
    description: "Literal board value to apply during synchronization."
  }),
  path: z.string().min(1).optional().meta({
    title: "Board Value Path",
    description: "Optional path referencing nested board data.",
    examples: ["customFields.squad"]
  })
}).strict().superRefine((value, ctx) => {
  if (value.field === "status" && !value.status) {
    ctx.addIssue({
      code: "custom",
      message: 'status is required when board field is "status"',
      path: ["status"]
    });
  }
  if (value.scope === "column" && !value.columnId) {
    ctx.addIssue({
      code: "custom",
      message: 'columnId is required when board scope is "column"',
      path: ["columnId"]
    });
  }
}).meta({
  title: "Board Integration Board Value",
  description: "Board-side value description that determines what the mapping should update."
});
var BoardIntegrationMapFiltersSchema = z.object({
  boardColumnIds: z.array(z.string().min(1)).optional().meta({
    title: "Board Column Filters",
    description: "Filters mappings to specific board column identifiers."
  }),
  providerTypes: z.array(z.string().min(1)).optional().meta({
    title: "Provider Type Filters",
    description: "Filters provider entities by their type identifiers.",
    examples: [["Bug", "Task"]]
  }),
  providerStatuses: z.array(z.string().min(1)).optional().meta({
    title: "Provider Status Filters",
    description: "Filters remote entities to only the listed provider statuses.",
    examples: [["Active", "Closed"]]
  }),
  iterationIds: z.array(z.string().min(1)).optional().meta({
    title: "Iteration Filters",
    description: "Restricts mappings to specific iteration identifiers.",
    examples: [["itr_1"]]
  }),
  query: z.string().min(1).optional().meta({
    title: "Filter Query",
    description: "Arbitrary query string used for server-side filtering.",
    examples: ["state:active"]
  })
}).strict().meta({
  title: "Board Integration Filters",
  description: "Optional filters that can limit when a mapping entry applies."
});
var BoardIntegrationMapSyncStrategySchema = z.object({
  onConflict: z.enum(["prefer-board", "prefer-provider", "merge", "most-recent", "fail"]).optional().meta({
    title: "Conflict Strategy",
    description: "Strategy applied when Rhythmic and provider records conflict during syncing.",
    examples: ["merge"]
  }),
  onMissingExternal: z.enum(["noop", "create", "archive"]).optional().meta({
    title: "Missing External Strategy",
    description: "Action taken when the provider record is missing but Rhythmic has a record.",
    examples: ["create"]
  }),
  onMissingBoard: z.enum(["noop", "delete", "detatch"]).optional().meta({
    title: "Missing Board Strategy",
    description: "Action taken when the Rhythmic board record is missing but the provider record remains.",
    examples: ["delete"]
  })
}).strict().meta({
  title: "Board Integration Sync Strategy",
  description: "Custom synchronization behavior overrides evaluated per mapping entry."
});
var queryStringSchema = z.string().trim().min(1).meta({
  title: "Saved Query Identifier",
  description: "Trimmed string containing a WIQL query or saved query identifier.",
  examples: ["Shared Queries/Blocked"]
});
var BoardIntegrationSavedImportPayloadBaseSchema = z.object({
  wiql: queryStringSchema.optional().meta({
    title: "WIQL Query",
    description: "Work Item Query Language statement used for imports."
  }),
  query: queryStringSchema.optional().meta({
    title: "Saved Query Key",
    description: "Identifier referencing a saved query defined in the provider."
  }),
  boardId: z.string().min(1).meta({
    title: "Board ID",
    description: "Rhythmic board identifier targeted by the import.",
    examples: ["board_abc"]
  }),
  packageId: z.string().min(1).meta({
    title: "Package ID",
    description: "Harmony package identifier associated with the import.",
    examples: ["pkg_123"]
  }),
  conflictResolutionMode: BoardIntegrationConflictResolutionModeSchema.optional().meta({
    title: "Import Conflict Mode",
    description: "Conflict handling strategy specific to the saved import."
  }),
  fieldMappings: z.array(BoardIntegrationFieldMappingSchema).default([]).meta({
    title: "Import Field Mappings",
    description: "Field mappings applied when executing the saved import."
  })
}).passthrough().meta({
  title: "Board Integration Saved Import Payload Base",
  description: "Common fields persisted for reusable board import payloads."
});
var BoardIntegrationSavedImportPayloadSchema = BoardIntegrationSavedImportPayloadBaseSchema.superRefine(
  (value, ctx) => {
    if (!value.wiql && !value.query) {
      ctx.addIssue({
        code: "custom",
        message: "Provide a WIQL query or saved query identifier for this import.",
        path: ["wiql"]
      });
    }
  }
).transform((value) => {
  const { query, wiql, ...rest } = value;
  const normalizedWiql = wiql ?? query;
  if (!normalizedWiql) throw new Error("Import payload is missing a WIQL query.");
  return {
    ...rest,
    wiql: normalizedWiql,
    ...query ? { query } : {}
  };
}).meta({
  title: "Board Integration Saved Import Payload",
  description: "Validated payload used when persisting saved import definitions."
});
var SavedImportMetadataSchema = z.object({
  providerId: z.string().min(1).meta({
    title: "Saved Import Provider ID",
    description: "Provider identifier used when the saved import was executed.",
    examples: ["prov_123"]
  }),
  integrationId: z.string().min(1).optional().meta({
    title: "Saved Import Integration ID",
    description: "Optional integration reference at the time the import ran."
  }),
  boardId: z.string().min(1).optional().meta({
    title: "Saved Import Board ID",
    description: "Optional board reference tied to the saved import metadata."
  }),
  packageId: z.string().min(1).optional().meta({
    title: "Saved Import Package ID",
    description: "Optional package reference captured alongside the import."
  }),
  payload: BoardIntegrationSavedImportPayloadSchema.meta({
    title: "Saved Import Payload",
    description: "Full payload that produced the saved import record."
  }),
  createdAt: z.coerce.date().optional().meta({
    title: "Saved Import Timestamp",
    description: "Timestamp indicating when the saved import metadata was recorded."
  })
}).strict().meta({
  title: "Saved Import Metadata",
  description: "Metadata persisted for saved import executions referenced by board mappings."
});
var BoardIntegrationMapEntrySchema = z.object({
  id: z.string().regex(/^[a-z0-9:-]{4,64}$/i, "id must be 4-64 characters (letters, numbers, colon, dash)").meta({
    title: "Map Entry ID",
    description: "Unique identifier for the mapping entry.",
    examples: ["map:status:ready"]
  }),
  direction: BoardIntegrationDirectionSchema.optional().meta({
    title: "Sync Direction",
    description: "Defines whether the mapping pushes to the provider, pulls from it, or stays in sync.",
    examples: ["sync"]
  }),
  rule: BoardIntegrationRuleSchema.optional().meta({
    title: "Mapping Rule",
    description: "Rule category that determines which board or provider fields the mapping applies to.",
    examples: ["status"]
  }),
  provider: BoardIntegrationMapProviderRefSchema.meta({
    title: "Provider Reference",
    description: "Provider details used to execute the mapping."
  }),
  boardValue: BoardIntegrationMapBoardValueSchema.meta({
    title: "Board Value Definition",
    description: "Board-side value definition the map entry updates."
  }),
  providerValue: BoardIntegrationMapProviderValueSchema.meta({
    title: "Provider Value Definition",
    description: "Provider-side value definition the map entry reads or writes."
  }),
  filters: BoardIntegrationMapFiltersSchema.optional().meta({
    title: "Entry Filters",
    description: "Optional filters restricting when the entry applies."
  }),
  syncStrategy: BoardIntegrationMapSyncStrategySchema.optional().meta({
    title: "Entry Sync Strategy",
    description: "Custom sync strategy overrides for the mapping entry."
  }),
  metadata: z.object({
    description: z.string().min(1).optional().meta({
      title: "Entry Description",
      description: "Human readable summary explaining the intent of the mapping entry.",
      examples: ["Sync Doing status to provider Active"]
    }),
    createdBy: z.string().min(1).optional().meta({
      title: "Created By",
      description: "Identifier of the user or automation that created the entry.",
      examples: ["user_123"]
    }),
    updatedAt: z.coerce.date().optional().meta({
      title: "Updated At",
      description: "Timestamp of the most recent update to the entry metadata."
    }),
    savedImport: SavedImportMetadataSchema.optional().meta({
      title: "Saved Import Metadata",
      description: "Optional reference to saved import metadata associated with the mapping entry."
    })
  }).strict().optional().meta({
    title: "Entry Metadata",
    description: "Optional metadata captured for auditing and UX hints."
  })
}).strict().meta({
  title: "Board Integration Map Entry",
  description: "Single mapping entry describing how a board value maps to/from a provider value."
});
var BoardIntegrationMapSchema = z.array(BoardIntegrationMapEntrySchema).meta({
  title: "Board Integration Map",
  description: "Ordered list of mapping entries composing the board integration mapping."
});
var BoardWorkitemSchemaInner = z10.object({
  id: z10.string().default(`bwi_${createId()}`).meta({
    title: "Board Workitem ID",
    description: "The unique identifier for the board workitem",
    example: "bwi_12345"
  }),
  workitem_id: z10.string().meta({
    title: "Workitem ID",
    description: "The associated workitem id",
    example: "wi_12345"
  }),
  column_id: z10.string().meta({
    title: "Column ID",
    description: "The column id on the board",
    example: "col_12345"
  }),
  position: z10.number().int().optional().default(0).meta({
    title: "Position",
    description: "Position/order in the column",
    example: 1
  }),
  board_id: z10.string().meta({
    title: "Board ID",
    description: "The board id",
    example: "b_12345"
  }),
  workspace_id: z10.string().nullable().meta({
    title: "Workspace ID",
    description: "Workspace id or null if none",
    example: null
  })
});
var BoardWorkitemSchema = BoardWorkitemSchemaInner;
var CreateBoardWorkitemPayloadSchema = BoardWorkitemSchemaInner.omit({
  id: true
});
var UpdateBoardWorkitemPayloadSchema = BoardWorkitemSchemaInner.partial().omit({
  id: true,
  workitem_id: true,
  board_id: true
});
var DeleteBoardWorkitemPayloadSchema = z10.object({
  id: z10.string().min(1, "Board Workitem ID is required").meta({
    title: "Board Workitem ID",
    description: "The ID of the board workitem to delete",
    example: "bwi_12345"
  })
});
var HarmonyPackageSchema = z10.object({
  id: z10.string().optional().default(`package_${createId()}`).meta({
    title: "Package ID",
    description: "The unique identifier for the harmony package",
    example: "package_12345"
  }),
  created_at: z10.coerce.date().default(/* @__PURE__ */ new Date()).meta({
    title: "Created At",
    description: "The timestamp when the harmony package was created",
    example: "2024-01-01T00:00:00.000Z"
  }),
  updated_at: z10.coerce.date().default(/* @__PURE__ */ new Date()).meta({
    title: "Updated At",
    description: "The timestamp when the harmony package was last updated",
    example: "2024-01-01T00:00:00.000Z"
  }),
  workspace_id: z10.string().min(1, "Workspace ID is required").meta({
    title: "Workspace ID",
    description: "The ID of the workspace the harmony package belongs to",
    example: "ws_12345"
  }),
  team_id: z10.string().min(1, "Team ID is required").meta({
    title: "Team ID",
    description: "The ID of the team the harmony package belongs to",
    example: "team_12345"
  }),
  title: z10.string().min(1, "Title is required").meta({
    title: "Title",
    description: "The title of the harmony package",
    example: "Project Alpha"
  }),
  context: z10.string().nullable().optional().default(null)
}).meta({
  title: "Harmony Package",
  description: "Schema for a harmony package including metadata and related entities"
});
var PublishStatusSchema = z10.enum(["draft", "published"]);
var HarmonyPackageDocumentSchemaInner = z10.object({
  id: z10.string().default(`document_${createId()}`).meta({
    title: "Harmony Package Document ID",
    description: "The unique identifier for the harmony package document",
    example: "hpd_12345"
  }),
  name: z10.string().min(1, "Document name is required").meta({
    title: "Document Name",
    description: "The name of the document",
    example: "Project Plan"
  }),
  image: z10.string().meta({
    title: "Document Image URL",
    description: "The URL of the document image",
    example: "https://example.com/image.png"
  }),
  context: z10.string().nullable().default(null).meta({
    title: "Document Context",
    description: "The context or content of the document",
    example: "This is the project plan document..."
  }),
  document: z10.any().nullable().default(null).meta({
    title: "Document Data",
    description: "The actual document data or file",
    example: null
  }),
  publish_status: PublishStatusSchema.default("draft").meta({
    title: "Publish Status",
    description: "The publish status of the document",
    example: "draft"
  }),
  created_at: z10.coerce.date().default(() => /* @__PURE__ */ new Date()).meta({
    title: "Created At",
    description: "The timestamp when the document was created",
    example: "2024-01-01T00:00:00.000Z"
  }),
  updated_at: z10.coerce.date().default(() => /* @__PURE__ */ new Date()).meta({
    title: "Updated At",
    description: "The timestamp when the document was last updated",
    example: "2024-01-01T00:00:00.000Z"
  }),
  package_id: z10.string().min(1, "Harmony Package ID is required").meta({
    title: "Harmony Package ID",
    description: "The ID of the harmony package the document belongs to",
    example: "hp_12345"
  }),
  last_indexed_at: z10.coerce.date().nullable().default(null).meta({
    title: "Last Indexed At",
    description: "The timestamp when the document was last indexed",
    example: null
  }),
  rag_one_liner: z10.string().nullable().default(null).meta({
    title: "RAG One Liner",
    description: "A one-liner summary generated by RAG",
    example: null
  }),
  rag_summary: z10.string().nullable().default(null).meta({
    title: "RAG Summary",
    description: "A detailed summary generated by RAG",
    example: null
  }),
  markdown: z10.string().nullable().default(null).meta({
    title: "Markdown Content",
    description: "The markdown representation of the document",
    example: null
  }),
  workspace_id: z10.string().meta({
    title: "Workspace ID",
    description: "The ID of the workspace",
    example: "ws_12345"
  }),
  folder_id: z10.string().nullable().default(null).meta({
    title: "Folder ID",
    description: "Identifier of the folder containing this document",
    example: "folder_12345"
  })
}).meta({
  title: "Harmony Package Document",
  description: "Schema for a harmony package document including metadata"
});
var HarmonyPackageDocumentSchema = HarmonyPackageDocumentSchemaInner;
var CreateHarmonyPackageDocumentPayloadSchema = HarmonyPackageDocumentSchemaInner.omit({
  id: true,
  created_at: true,
  updated_at: true,
  last_indexed_at: true
});
var UpdateHarmonyPackageDocumentPayloadSchema = z10.object({
  name: z10.string().min(1, "Document name is required").optional(),
  image: z10.string().nullable().optional(),
  context: z10.string().nullable().optional(),
  publish_status: PublishStatusSchema.optional(),
  rag_one_liner: z10.string().nullable().optional(),
  rag_summary: z10.string().nullable().optional(),
  markdown: z10.string().nullable().optional(),
  folder_id: z10.string().nullable().optional()
});
var DeleteHarmonyPackageDocumentPayloadSchema = z10.object({
  id: z10.string().min(1, "Harmony Package Document ID is required").meta({
    title: "Harmony Package Document ID",
    description: "The ID of the harmony package document to delete",
    example: "hpd_12345"
  })
});
var HarmonyPackageFolderSchema = z10.object({
  id: z10.string().default(() => `folder_${createId()}`).meta({
    title: "Folder ID",
    description: "Identifier for the harmony package folder",
    example: "folder_12345"
  }),
  name: z10.string().min(1).meta({
    title: "Folder Name",
    description: "Display name for the folder",
    example: "Design Docs"
  }),
  description: z10.string().nullable().default(null).meta({
    title: "Description",
    description: "Optional description for the folder",
    example: "Planning documents for Q1"
  }),
  created_at: z10.coerce.date().default(() => /* @__PURE__ */ new Date()).meta({
    title: "Created At",
    description: "Timestamp when the folder was created",
    example: "2024-01-01T00:00:00.000Z"
  }),
  updated_at: z10.coerce.date().default(() => /* @__PURE__ */ new Date()).meta({
    title: "Updated At",
    description: "Timestamp when the folder was last updated",
    example: "2024-01-02T00:00:00.000Z"
  }),
  package_id: z10.string().min(1).meta({
    title: "Package ID",
    description: "Identifier of the harmony package that owns the folder",
    example: "package_12345"
  }),
  parent_id: z10.string().nullable().default(null).meta({
    title: "Parent Folder ID",
    description: "Identifier of the parent folder if nested",
    example: "folder_67890"
  }),
  workspace_id: z10.string().nullable().default(null).meta({
    title: "Workspace ID",
    description: "Workspace identifier used for filtering",
    example: "workspace_12345"
  })
}).meta({
  title: "Harmony Package Folder",
  description: "Schema describing a harmony package folder"
});
var HarmonyWorkitemSchemaInner = z10.object({
  id: z10.string().optional().default(`workitem_${createId()}`).meta({
    title: "Workitem ID",
    description: "The unique identifier for the harmony workitem",
    example: "workitem_12345"
  }),
  harmony_package_id: z10.string().min(1, "Harmony Package ID is required").meta({
    title: "Harmony Package ID",
    description: "The ID of the harmony package the workitem belongs to",
    example: "package_12345"
  }),
  workitem_id: z10.string().min(1, "Workitem ID is required").meta({
    title: "Workitem ID",
    description: "The ID of the workitem",
    example: "wi_12345"
  }),
  workspace_id: z10.string().meta({
    title: "Workspace ID",
    description: "The ID of the workspace",
    example: "ws_12345"
  })
}).meta({
  title: "Harmony Workitem",
  description: "Schema for a harmony workitem including metadata"
});
var HarmonyWorkitemSchema = HarmonyWorkitemSchemaInner;
var CreateHarmonyWorkitemPayloadSchema = HarmonyWorkitemSchemaInner.omit({
  id: true
});
var UpdateHarmonyWorkitemPayloadSchema = HarmonyWorkitemSchemaInner.partial().omit({
  id: true,
  harmony_package_id: true,
  workitem_id: true
});
var DeleteHarmonyWorkitemPayloadSchema = z10.object({
  id: z10.string().min(1, "Workitem ID is required").meta({
    title: "Workitem ID",
    description: "The ID of the harmony workitem to delete",
    example: "workitem_12345"
  })
});
var DecimalString = z10.string().regex(/^-?\d+(\.\d+)?$/, "Invalid decimal format").transform((val) => new Decimal(val));
var HarmonyPackageBoardSchema = z10.object({
  id: z10.string().default(() => `board_${createId()}`),
  name: z10.string().min(1).max(100).meta({
    title: "Board Name",
    description: "The name of the harmony package board",
    example: "Sprint Planning Board"
  }),
  image: z10.string().nullable().default(null).meta({
    title: "Board Image URL",
    description: "The URL of the board image",
    example: "https://example.com/board-image.png"
  }),
  publish_status: PublishStatusSchema.default("draft").meta({
    title: "Publish Status",
    description: "The publish status of the board",
    example: "draft"
  }),
  created_at: z10.coerce.date().default(() => /* @__PURE__ */ new Date()).meta({
    title: "Created At",
    description: "The timestamp when the board was created",
    example: "2024-01-01T00:00:00.000Z"
  }),
  updated_at: z10.coerce.date().default(() => /* @__PURE__ */ new Date()).meta({
    title: "Updated At",
    description: "The timestamp when the board was last updated",
    example: "2024-01-01T00:00:00.000Z"
  }),
  package_id: z10.string().min(1).meta({
    title: "Harmony Package ID",
    description: "The ID of the harmony package the board belongs to",
    example: "hp_12345"
  }),
  context: z10.string().nullable().default(null).meta({
    title: "Board Context",
    description: "The context or description of the board",
    example: "This board is used for sprint planning..."
  }),
  cost_per_iteration: DecimalString.nullable().default(null).meta({
    title: "Cost Per Iteration",
    description: "The cost associated with each iteration on the board",
    example: 5e3
  }),
  forecast_burndown: z10.json().nullable().default(null).meta({
    title: "Forecast Burndown",
    description: "The forecast burndown data for the board",
    example: null
  }),
  integrations_map: BoardIntegrationMapSchema.nullable().default(null).meta({
    title: "Integrations Map",
    description: "Mapping definitions for synchronizing with external integrations",
    example: [
      {
        provider: { integrationId: "teamIntegration:jira", key: "jira" },
        rule: "status",
        direction: "sync",
        boardValue: {
          scope: "column",
          field: "status",
          status: "Doing",
          columnId: "column_doing"
        },
        providerValue: {
          entity: "issue",
          field: "fields.status.name",
          value: "In Progress"
        }
      }
    ]
  }),
  forecast_end_date: z10.coerce.date().nullable().default(null).meta({
    title: "Forecast End Date",
    description: "The forecasted end date for the board",
    example: "2024-03-01T00:00:00.000Z"
  }),
  forecast_start_date: z10.coerce.date().nullable().default(null).meta({
    title: "Forecast Start Date",
    description: "The forecasted start date for the board",
    example: "2024-01-15T00:00:00.000Z"
  }),
  iteration_id: z10.string().nullable().default(null).meta({
    title: "Iteration ID",
    description: "The ID of the iteration associated with the board",
    example: "it_12345"
  }),
  workspace_id: z10.string().meta({
    title: "Workspace ID",
    description: "The ID of the workspace",
    example: "ws_12345"
  }),
  folder_id: z10.string().nullable().default(null).meta({
    title: "Folder ID",
    description: "Identifier of the folder containing this board",
    example: "folder_12345"
  }),
  package: z10.any().nullable().default(null).meta({
    title: "Harmony Package",
    description: "The associated harmony package object",
    example: null
  })
});

// src/schemas/harmonyPackage.registry.ts
var HarmonyPackageRegistry = z10.registry();
HarmonyPackageRegistry.add(HarmonyPackageSchema);
HarmonyPackageRegistry.add(BoardWorkitemSchema);
HarmonyPackageRegistry.add(HarmonyWorkitemSchema);
HarmonyPackageRegistry.add(HarmonyPackageDocumentSchema);
HarmonyPackageRegistry.add(HarmonyPackageFolderSchema);

// src/utils/uuid.ts
function buildUuidWithRandomValues(bytes) {
  const hex = Array.from(bytes).map((byte) => byte.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
}
function buildUuidWithMathRandom() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = Math.random() * 16 | 0;
    const value = char === "x" ? random : random & 3 | 8;
    return value.toString(16);
  });
}
function generateUuid() {
  const cryptoObj = globalThis?.crypto;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") return cryptoObj.randomUUID();
  if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
    const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
    bytes[6] = bytes[6] & 15 | 64;
    bytes[8] = bytes[8] & 63 | 128;
    return buildUuidWithRandomValues(bytes);
  }
  return buildUuidWithMathRandom();
}

// src/schemas/kanbanBoardItem.schema.ts
var CreateKanbanBoardItemSchema = z10.object({
  position: z10.number().nullish().default(0).meta({
    title: "Position",
    description: "The position of the board item in the column",
    example: 1
  }),
  columnId: z10.string().min(5, "Column ID is required").meta({
    title: "Column ID",
    description: "The ID of the column the item belongs to",
    example: "col_12345"
  }),
  workitem: z10.object({
    title: z10.string().min(1, "Title is required").meta({
      title: "Title",
      description: "The title of the board item",
      example: "Implement authentication"
    }),
    parent_id: z10.string().nullable().optional().meta({
      title: "Parent ID",
      description: "The ID of the parent work item if any",
      example: "workitem_12345"
    }),
    workitem_type: z10.string().default("Story").meta({
      title: "Workitem Type",
      description: "The type of the work item",
      example: "Story"
    }),
    status: z10.string().nullish().meta({
      title: "Workitem Status",
      description: "The status of the work item",
      example: "Not Ready"
    }),
    description: z10.string().nullish().meta({
      title: "Description",
      description: "The description of the work item",
      example: "Implement authentication"
    }),
    acceptance_criteria: z10.string().nullish().meta({
      title: "Acceptance Criteria",
      description: "The acceptance criteria for the work item",
      example: "User can log in and log out"
    }),
    effort: z10.number().nullish().meta({
      title: "Effort",
      description: "The effort estimate for the work item",
      example: 5
    }),
    status_code: z10.string().nullish().meta({
      title: "Status Code",
      description: "The code representing the status if different from display status",
      example: "NOT_READY"
    }),
    iteration_path: z10.string().nullish().meta({
      title: "Iteration Path",
      description: "The iteration path the work item is assigned to",
      example: "Sprint 1"
    }),
    current_iteration: z10.string().nullish().meta({
      title: "Current Iteration",
      description: "The current iteration ID the work item is in",
      example: "iter_12345"
    }),
    priority: z10.number().positive().nullable().optional().meta({
      title: "Priority",
      description: "The priority of the work item",
      example: 1
    }),
    moscow: z10.enum(["Must Have", "Should Have", "Could Have", "Won't Have"]).nullish().meta({
      title: "MoSCoW",
      description: "The MoSCoW priority of the work item",
      example: "Must Have"
    }),
    business_value: z10.number().nullish().meta({
      title: "Business Value",
      description: "The business value of the work item",
      example: 100
    }),
    integration_id: z10.string().nullish().meta({
      title: "Integration ID",
      description: "The ID of the integration the work item is part of",
      example: "int_12345"
    }),
    external_system_id: z10.string().nullish().meta({
      title: "External System ID",
      description: "The ID of the external system the work item is linked to",
      example: "ext_12345"
    })
  }).meta({
    title: "Work Item",
    description: "The work item associated with the board item"
  })
});
var AttachKanbanBoardItemSchema = z10.object({
  boardId: z10.string().min(5, "Board ID is required").meta({
    title: "Board ID",
    description: "Identifier of the board to attach to",
    example: "board_12345"
  }),
  harmonyWorkitemId: z10.string().min(5, "Harmony workitem ID is required").meta({
    title: "Harmony Workitem ID",
    description: "Identifier of the harmony workitem to attach",
    example: "hwi_12345"
  }),
  columnId: z10.string().min(5, "Column ID is required").meta({
    title: "Column ID",
    description: "Column receiving the workitem",
    example: "col_67890"
  }),
  position: z10.coerce.number().int().nonnegative().optional().meta({
    title: "Position",
    description: "Optional explicit position for the workitem",
    example: 4
  }),
  boardWorkitemId: z10.string().min(5).optional().meta({
    title: "Board Workitem ID",
    description: "Optional explicit identifier for the board workitem record",
    example: "bwi_12345"
  })
}).meta({
  title: "Attach Kanban Board Item",
  description: "Schema for attaching an existing harmony workitem to a kanban board"
});
var KanbanExistingHarmonyItemSchema = z10.object({
  hid: z10.string().min(5, "Harmony workitem ID is required").meta({
    title: "Harmony Workitem ID",
    description: "Identifier of the Harmony workitem to attach",
    example: "hwi_12345"
  }),
  column_id: z10.string().min(5, "Column ID is required to place the workitem").meta({
    title: "Column ID",
    description: "Column receiving the workitem",
    example: "col_67890"
  })
}).meta({
  title: "Existing Harmony Item",
  description: "Minimal data required from a Harmony item to attach it to a Kanban board column"
});
var KanbanBoardWorkitemInsertSchema = z10.object({
  id: z10.string().min(5, "Board workitem ID is required").meta({
    title: "Board Workitem ID",
    description: "Unique identifier for the board workitem record",
    example: "bwi_12345"
  }),
  board_id: z10.string().min(5, "Board ID is required").meta({
    title: "Board ID",
    description: "Identifier of the board receiving the workitem",
    example: "board_12345"
  }),
  column_id: z10.string().min(5, "Column ID is required").meta({
    title: "Column ID",
    description: "Target column for the workitem",
    example: "col_67890"
  }),
  workitem_id: z10.string().min(5, "Harmony workitem ID is required").meta({
    title: "Harmony Workitem ID",
    description: "Identifier of the Harmony workitem being attached",
    example: "hwi_12345"
  }),
  position: z10.number().nonnegative().optional().meta({
    title: "Position",
    description: "Relative position within the column",
    example: 12
  }),
  workspace_id: z10.string().min(5, "Workspace ID is required").nullable().optional().meta({
    title: "Workspace ID",
    description: "Workspace identifier associated with the board item",
    example: "workspace_12345"
  })
}).meta({
  title: "Kanban Board Workitem Insert",
  description: "Schema representing a board workitem insert payload used for optimistic updates"
});
var TagReferenceSchema = z10.union([
  z10.uuid("Tag ID must be a valid UUID"),
  z10.object({
    id: z10.uuid("Tag ID must be a valid UUID"),
    workitemTagId: z10.string().optional(),
    tagId: z10.uuid("Team tag ID must be a valid UUID").optional()
  })
]);
var UpdateKanbanWorkitemPatchSchema = z10.object({
  workitem_status: z10.string().min(1).optional().meta({
    title: "Workitem Status",
    description: "Updated status for the workitem",
    example: "In Progress"
  }),
  status_code: z10.string().min(1).optional().meta({
    title: "Status Code",
    description: "Updated status code for the workitem if different from the display status",
    example: "In_Progress"
  }),
  parent_workitem: z10.string().nullable().optional().meta({
    title: "Parent ID",
    description: "Updated parent relationship for the workitem",
    example: "workitem_parent"
  }),
  priority: z10.number().nullable().optional().meta({
    title: "Priority",
    description: "Updated priority value for the workitem",
    example: 1
  }),
  title: z10.string().min(1).optional().meta({
    title: "Title",
    description: "Updated title for the workitem",
    example: "Improve authentication UX"
  }),
  description: z10.string().nullable().optional().meta({
    title: "Description",
    description: "Updated description for the workitem",
    example: "Refine login error handling and UX"
  }),
  acceptance_criteria: z10.string().nullable().optional().meta({
    title: "Acceptance Criteria",
    description: "Updated acceptance criteria for the workitem",
    example: "App handles login failures gracefully"
  }),
  business_value: z10.number().nullable().optional().meta({
    title: "Business Value",
    description: "Updated business value for the workitem",
    example: 21
  }),
  effort: z10.number().nullable().optional().meta({
    title: "Effort",
    description: "Updated effort estimate",
    example: 5
  }),
  workitem_type: z10.string().nullish().meta({
    title: "Workitem Type",
    description: "Updated type for the workitem",
    example: "Bug"
  }),
  current_iteration: z10.string().nullable().optional().meta({
    title: "Current Iteration",
    description: "Updated current iteration ID",
    example: "iter_67890"
  }),
  iteration_path: z10.string().nullable().optional().meta({
    title: "Iteration Path",
    description: "Updated iteration path for the workitem",
    example: "Iteration/Sprint 3"
  }),
  moscow: z10.string().nullable().optional().meta({
    title: "MoSCoW Priority",
    description: "Updated MoSCoW priority",
    example: "Must_Have"
  }),
  acceptance_criteria_markdown: z10.string().nullable().optional().meta({
    title: "Acceptance Criteria Markdown",
    description: "Updated AC markdown",
    example: "- User sees detailed error messages"
  }),
  description_markdown: z10.string().nullable().optional().meta({
    title: "Description Markdown",
    description: "Updated description markdown",
    example: "- Update UX copy for login"
  }),
  integration_id: z10.string().nullable().optional().meta({
    title: "Integration ID",
    description: "Updated integration ID",
    example: "int_12345"
  }),
  external_system_id: z10.string().nullable().optional().meta({
    title: "External System ID",
    description: "Updated external system ID",
    example: "ext_12345"
  }),
  activated_date: z10.coerce.date().nullable().optional().meta({
    title: "Activated Date",
    description: "Updated activated date",
    example: "2023-02-01T00:00:00Z"
  }),
  closed_date: z10.coerce.date().nullable().optional().meta({
    title: "Closed Date",
    description: "Updated closed date",
    example: "2023-03-01T00:00:00Z"
  }),
  last_synced_date: z10.coerce.date().nullable().optional().meta({
    title: "Last Synced Date",
    description: "Updated last sync date",
    example: "2023-01-20T00:00:00Z"
  }),
  is_deleted: z10.boolean().optional().meta({
    title: "Deleted Flag",
    description: "Soft delete flag for the workitem",
    example: false
  }),
  tags: z10.object({
    add: z10.array(TagReferenceSchema).default([]),
    remove: z10.array(TagReferenceSchema).default([]),
    create: z10.array(
      z10.object({
        id: z10.uuid("Tag ID must be a valid UUID").optional().default(generateUuid),
        tag: z10.string().min(1, "Tag label is required"),
        color: z10.string().nullable().optional()
      })
    ).default([])
  }).optional().meta({
    title: "Tag Mutations",
    description: "Tag mutations to apply to the workitem",
    example: { add: [{ tag_id: "uuid" }], remove: [], create: [] }
  })
}).meta({
  title: "Update Workitem Patch",
  description: "Allowable fields when mutating a workitem via the Kanban board item updater"
});
var KanbanWorkitemMutationSchema = z10.object({
  workitemId: z10.uuid("Workitem ID must be a valid UUID"),
  boardId: z10.string().min(5, "Board ID is required").nullish(),
  boardItemId: z10.string().min(5, "Board item ID is required").nullish(),
  columnId: z10.string().min(5).nullish(),
  position: z10.number().optional(),
  harmonyWorkitemId: z10.string().min(5).optional(),
  workspaceId: z10.uuid("Workspace ID must be a valid UUID").nullable().optional(),
  mutations: UpdateKanbanWorkitemPatchSchema.optional().default({})
}).meta({
  title: "Kanban Workitem Mutation",
  description: "Mutation payload for updating workitems within the kanban context"
});
var UpdateKanbanBoardItemSchema = z10.object({
  boardItemId: z10.string().min(5, "Board item ID is required").meta({
    title: "Board Item ID",
    description: "Identifier of the board item to update",
    example: "board_item_12345"
  }),
  column_id: z10.string().min(5).optional().meta({
    title: "Column ID",
    description: "New column for the board item",
    example: "col_67890"
  }),
  position: z10.coerce.number().optional().meta({
    title: "Position",
    description: "Updated column position",
    example: 5
  }),
  workitem_id: z10.string().min(5).optional().meta({
    title: "Workitem ID",
    description: "Identifier of the associated workitem if known",
    example: "workitem_12345"
  }),
  workitem: UpdateKanbanWorkitemPatchSchema.optional()
}).refine((value) => {
  if (value.column_id !== void 0 || value.position !== void 0) return true;
  if (!value.workitem) return false;
  const workitemPatch = value.workitem;
  return workitemPatch.workitem_status !== void 0 || workitemPatch.status_code !== void 0 || Object.hasOwn(workitemPatch, "parent_workitem") || Object.hasOwn(workitemPatch, "priority");
}, "At least one update (columnId, position, or workitem fields) must be provided").meta({
  title: "Update Kanban Board Item",
  description: "Schema for updating an existing kanban board item"
});
var DeleteKanbanBoardItemSchema = z10.object({
  boardItemId: z10.string().min(5, "Board item ID is required").meta({
    title: "Board Item ID",
    description: "Identifier of the board item to delete",
    example: "board_item_12345"
  })
}).meta({
  title: "Delete Kanban Board Item",
  description: "Schema for deleting a kanban board item"
});
var boardImageSchema = z10.string().trim().min(1).superRefine((value, ctx) => {
  const trimmed = value.trim();
  if (trimmed === "") {
    ctx.addIssue({
      code: z10.ZodIssueCode.custom,
      message: "Image cannot be empty"
    });
    return;
  }
  if (trimmed.startsWith("data:")) return;
  if (trimmed.startsWith("[") && trimmed.endsWith("]")) return;
  try {
    const url2 = new URL(trimmed);
    if (url2.protocol === "http:" || url2.protocol === "https:") return;
  } catch {
  }
  ctx.addIssue({
    code: z10.ZodIssueCode.custom,
    message: "Image must be an http(s) URL, data URI, or serialized binary string array."
  });
});
var decimalLikeSchema = z10.union([z10.number(), z10.string().regex(/^-?\d+(\.\d+)?$/, "Invalid decimal format")]).transform((value) => {
  if (typeof value === "number") return value.toString();
  return value;
});
var UpdateKanbanBoardSchema = z10.object({
  name: z10.string().min(1).optional().meta({
    title: "Name",
    description: "Updated name of the board",
    example: "Sprint 1 Board"
  }),
  image: boardImageSchema.nullable().optional().meta({
    title: "Board Image",
    description: "Updated image for the board. Accepts http(s) URLs, data URIs, or serialized binary string arrays.",
    example: "https://example.com/board-image.png"
  }),
  context: z10.string().nullable().optional().meta({
    title: "Context",
    description: "Updated context or description for the board",
    example: "Board for sprint 1 tasks"
  }),
  iteration_id: z10.string().min(5).nullable().optional().meta({
    title: "Iteration ID",
    description: "Updated iteration ID associated with the board",
    example: "iter_12345"
  }),
  publish_status: z10.string().min(3).nullable().optional().meta({
    title: "Publish Status",
    description: "Updated publish status of the board",
    example: "Published"
  }),
  forecast_start_date: z10.coerce.date().nullable().optional().meta({
    title: "Forecast Start Date",
    description: "Updated forecast start date in ISO format",
    example: "2023-10-01T00:00:00Z"
  }),
  forecast_end_date: z10.coerce.date().nullable().optional().meta({
    title: "Forecast End Date",
    description: "Updated forecast end date in ISO format",
    example: "2023-10-15T00:00:00Z"
  }),
  forecast_burndown: z10.array(z10.number()).nullable().optional().meta({
    title: "Forecast Burndown",
    description: "Updated forecast burndown data points",
    example: [100, 80, 60, 40, 20, 0]
  }),
  integrations_map: BoardIntegrationMapSchema.nullable().optional().meta({
    title: "Integrations Map",
    description: "Updated integration mapping definitions for external systems",
    example: [
      {
        provider: { integrationId: "teamIntegration:jira", key: "jira" },
        rule: "status",
        direction: "sync",
        boardValue: {
          scope: "column",
          field: "status",
          status: "Doing",
          columnId: "column_doing"
        },
        providerValue: {
          entity: "issue",
          field: "fields.status.name",
          value: "In Progress"
        }
      }
    ]
  }),
  cost_per_iteration: decimalLikeSchema.nullable().optional().meta({
    title: "Cost Per Iteration",
    description: "Updated cost per iteration for the board",
    example: 5e3
  })
}).meta({
  title: "Update Kanban Board",
  description: "Schema for updating an existing kanban board"
});
var CreateKanbanColumnSchema = z10.object({
  id: z10.string().min(5).default(() => `kanban_column_${createId()}`).meta({
    title: "Column ID",
    description: "Unique identifier for the kanban column",
    example: "kanban_column_x1y2z3"
  }),
  name: z10.string().min(1, "Column name is required").meta({
    title: "Column Name",
    description: "The display name for the column",
    example: "In Progress"
  }),
  statusType: z10.string().min(1, "Status type is required").meta({
    title: "Status Type",
    description: "Status classification for the column",
    example: "IN_PROGRESS"
  }),
  position: z10.coerce.number().positive().default(0).meta({
    title: "Position",
    description: "Column ordering index",
    example: 2
  }),
  boardId: z10.string().min(5, "Board ID is required").meta({
    title: "Board ID",
    description: "The board the column belongs to",
    example: "board_12345"
  }),
  workspaceId: z10.string().min(5).optional().meta({
    title: "Workspace ID",
    description: "Optional workspace identifier for the column",
    example: "ws_12345"
  }),
  harmonyPackageId: z10.string().min(5, "Package ID is required").meta({
    title: "Harmony Package ID",
    description: "Harmony package the board belongs to",
    example: "pkg_12345"
  })
}).meta({
  title: "Create Kanban Column",
  description: "Schema for creating a new kanban column"
});
var UpdateKanbanColumnSchemaBase = z10.object({
  columnId: z10.string().min(5, "Column ID is required").meta({
    title: "Column ID",
    description: "Identifier of the column being updated",
    example: "kanban_column_x1y2z3"
  }),
  name: z10.string().min(1).optional().meta({
    title: "Column Name",
    description: "Updated column display name",
    example: "Review"
  }),
  statusType: z10.string().min(1).optional().meta({
    title: "Status Type",
    description: "Updated status classification for the column",
    example: "IN_REVIEW"
  }),
  position: z10.coerce.number().positive().optional().meta({
    title: "Position",
    description: "Updated ordering index",
    example: 3
  })
}).refine(
  (value) => value.name !== void 0 || value.statusType !== void 0 || value.position !== void 0,
  "At least one updatable field (name, statusType, position) must be provided"
);
var UpdateKanbanColumnSchema = UpdateKanbanColumnSchemaBase.meta({
  title: "Update Kanban Column",
  description: "Schema for updating an existing kanban column"
});
var DeleteKanbanColumnSchema = z10.object({
  columnId: z10.string().min(5, "Column ID is required").meta({
    title: "Column ID",
    description: "Identifier of the column being removed",
    example: "kanban_column_x1y2z3"
  }),
  moveColumnId: z10.string().min(5).optional().meta({
    title: "Move To Column ID",
    description: "Optional column ID to move existing items into before deletion",
    example: "kanban_column_a1b2c3"
  })
}).meta({
  title: "Delete Kanban Column",
  description: "Schema for deleting a kanban column"
});
var KanbanContextSchema = z10.object({
  boardId: z10.string().min(5, "Board ID is required").meta({
    title: "Board ID",
    description: "The ID of the kanban board",
    example: "board_12345"
  }),
  packageId: z10.string().min(5, "Package ID is required").meta({
    title: "Package ID",
    description: "The ID of the harmony package",
    example: "pkg_12345"
  }),
  workspaceId: z10.string().min(5, "Workspace ID is required").meta({
    title: "Workspace ID",
    description: "The ID of the workspace",
    example: "ws_12345"
  }),
  teamId: z10.string().min(5, "Team ID is required").meta({
    title: "Team ID",
    description: "The ID of the team",
    example: "team_12345"
  })
}).meta({
  title: "Kanban Context",
  description: "The context for the kanban board including board, package, workspace, and team IDs"
});

// src/schemas/kanban.registry.ts
var KanbanSchemaRegistry = z10.registry();
KanbanSchemaRegistry.add(CreateKanbanBoardItemSchema);
KanbanSchemaRegistry.add(KanbanContextSchema);
KanbanSchemaRegistry.add(CreateKanbanColumnSchema);
KanbanSchemaRegistry.add(UpdateKanbanColumnSchema);
KanbanSchemaRegistry.add(DeleteKanbanColumnSchema);
KanbanSchemaRegistry.add(UpdateKanbanBoardItemSchema);
KanbanSchemaRegistry.add(DeleteKanbanBoardItemSchema);
KanbanSchemaRegistry.add(AttachKanbanBoardItemSchema);
KanbanSchemaRegistry.add(KanbanExistingHarmonyItemSchema);
KanbanSchemaRegistry.add(KanbanBoardWorkitemInsertSchema);
KanbanSchemaRegistry.add(KanbanWorkitemMutationSchema);
var TeamSchemaInner = z10.object({
  id: z10.string().default(`team_${createId()}`).meta({
    title: "Team Tag ID",
    description: "Unique identifier for the team tag",
    example: "team_12345"
  }),
  created_at: z10.coerce.date().meta({
    title: "Created At",
    description: "Timestamp when the team was created",
    example: "2023-01-01T00:00:00Z"
  }),
  updated_at: z10.coerce.date().meta({
    title: "Updated At",
    description: "Timestamp when the team was last updated",
    example: "2023-01-01T00:00:00Z"
  }),
  name: z10.string().nullable().meta({
    title: "Team Name",
    description: "Display name for the team",
    example: "My Team"
  }),
  workspace_id: z10.uuid().meta({
    title: "Workspace ID",
    description: "Unique identifier for the workspace",
    example: "123e4567-e89b-12d3-a456-426614174000"
  }),
  workspace_slug: z10.string().min(1, "Workspace slug is required").meta({
    title: "Workspace Slug",
    description: "URL-friendly identifier for the workspace this team belongs to",
    example: "my-workspace"
  }),
  avatar_url: z10.string().nullable().default(null).meta({
    title: "Avatar URL",
    description: "URL of the team avatar image",
    example: "https://example.com/avatar.png"
  }),
  slug: z10.string().min(1, "Team slug is required").meta({
    title: "Team Slug",
    description: "URL-friendly identifier for the team",
    example: "my-team"
  }),
  context: z10.string().nullable().default(null).meta({
    title: "Context",
    description: "Contextual information about the team",
    example: "This team works on project X."
  })
});
var TeamSchema = TeamSchemaInner;
var CreateTeamSchema = TeamSchemaInner.omit({
  id: true,
  created_at: true,
  updated_at: true,
  workspace_id: true,
  workspace_slug: true
});
var UpdateTeamSchema = TeamSchemaInner.pick({
  slug: true,
  name: true,
  avatar_url: true,
  context: true
}).extend({
  context: z10.string().nullish(),
  slug: z10.string().min(1, "Team slug is required").nullish(),
  avatar_url: z10.string().nullish(),
  name: z10.string().nullish()
});
var TeamIterationStatusSchema = z10.enum(["New", "Future", "Committed", "Current", "Past", "Cancelled"]).meta({
  title: "Team Iteration Status",
  description: "Current status of the team iteration",
  example: "active"
});
var IterationBaseSchema = z10.object({
  id: z10.uuid().default(generateUuid).meta({
    title: "Iteration ID",
    description: "Unique identifier for the iteration",
    example: "iteration_12345"
  }),
  team_id: z10.string().meta({
    title: "Team ID",
    description: "Identifier of the team this iteration belongs to",
    example: "team_12345"
  }),
  name: z10.string().min(1, "Iteration name is required").meta({
    title: "Iteration Name",
    description: "Name of the iteration",
    example: "Sprint 1"
  }),
  start_date: z10.coerce.date().meta({
    title: "Start Date",
    description: "Start date of the iteration",
    example: "2023-01-01T00:00:00Z"
  }),
  end_date: z10.coerce.date().meta({
    title: "End Date",
    description: "End date of the iteration",
    example: "2023-01-15T00:00:00Z"
  }),
  person_days: z10.number().nullable().default(null).meta({
    title: "Person Days",
    description: "Number of person days allocated for this iteration",
    example: 10
  }),
  interrupt_buffer: z10.number().nullable().default(null).meta({
    title: "Interrupt Buffer",
    description: "Buffer allocation for interrupts",
    example: 2
  }),
  interrupts_complete: z10.number().nullable().default(null).meta({
    title: "Interrupts Complete",
    description: "Number of interrupts completed",
    example: 1
  }),
  extra_value_complete: z10.number().nullable().default(null).meta({
    title: "Extra Value Complete",
    description: "Extra value work completed",
    example: 5
  }),
  planned_points_to_accept: z10.number().nullable().default(null).meta({
    title: "Planned Points To Accept",
    description: "Points planned to be accepted in this iteration",
    example: 50
  }),
  yesterdays_weather: z10.number().nullable().default(null).meta({
    title: "Yesterdays Weather",
    description: "Yesterday's weather metric",
    example: 48
  }),
  points_carried_over: z10.number().nullable().default(null).meta({
    title: "Points Carried Over",
    description: "Points carried over from previous iteration",
    example: 5
  }),
  updated_at: z10.coerce.date().default(/* @__PURE__ */ new Date()).meta({
    title: "Updated At",
    description: "Timestamp when the iteration was last updated",
    example: "2023-01-10T00:00:00Z"
  }),
  created_at: z10.coerce.date().default(/* @__PURE__ */ new Date()).meta({
    title: "Created At",
    description: "Timestamp when the iteration was created",
    example: "2023-01-01T00:00:00Z"
  }),
  created_by: z10.string().default("notset").meta({
    title: "Created By",
    description: "User ID who created this iteration",
    example: "user_12345"
  }),
  planned_complete: z10.number().nullable().default(null).meta({
    title: "Planned Complete",
    description: "Points planned to be completed in this iteration",
    example: 50
  }),
  points_per_person_days: z10.number().default(0).meta({
    title: "Points Per Person Days",
    description: "Ratio of points to person days",
    example: 5
  }),
  status: TeamIterationStatusSchema.meta({
    title: "Status",
    description: "Current status of the iteration",
    example: "active"
  }),
  yesterdays_weather_points_per_person_days: z10.number().default(0).meta({
    title: "Yesterdays Weather Points Per Person Days",
    description: "Points per person day based on yesterday's weather",
    example: 4.8
  })
});
function withIterationDateRange(schema) {
  return schema.superRefine((data, ctx) => {
    const { start_date, end_date } = data;
    if (!(start_date instanceof Date) || !(end_date instanceof Date)) {
      return;
    }
    if (end_date < start_date) {
      ctx.addIssue({
        code: "custom",
        message: "End date must be after start date",
        path: ["end_date"]
      });
    }
  });
}
var IterationSchema = withIterationDateRange(IterationBaseSchema).meta({
  title: "Iteration",
  description: "Schema for team iterations"
});
var CreateIterationSchema = withIterationDateRange(
  IterationBaseSchema.omit({
    id: true,
    created_at: true,
    updated_at: true,
    created_by: true
  }).partial()
);
var UpdateIterationSchema = withIterationDateRange(
  IterationBaseSchema.pick({
    name: true,
    start_date: true,
    end_date: true,
    person_days: true,
    interrupt_buffer: true,
    interrupts_complete: true,
    extra_value_complete: true,
    planned_points_to_accept: true,
    yesterdays_weather: true,
    points_carried_over: true,
    planned_complete: true,
    status: true,
    yesterdays_weather_points_per_person_days: true
  }).partial()
);
var TeamReferenceStorySchemaInner = z10.object({
  id: z10.uuid().default(generateUuid).meta({
    title: "Team Reference Story ID",
    description: "Unique identifier for the team reference story",
    example: "team_ref_story_12345"
  }),
  team_id: z10.string().meta({
    title: "Team ID",
    description: "Identifier of the team",
    example: "team_12345"
  }),
  created_by: z10.uuid().nullable().meta({
    title: "Created By",
    description: "User ID who created this reference story",
    example: "user_12345"
  }),
  story: z10.string().min(3, "More context is required").meta({
    title: "Story",
    description: "The reference story text",
    example: "As a user, I want to ..."
  }),
  estimate: z10.number().min(0, "Estimate must be a positive number").meta({
    title: "Estimate",
    description: "Estimate for the reference story",
    example: 3
  }),
  description: z10.string().min(10, "More context is required").nullable().meta({
    title: "Description",
    description: "Description of the reference story",
    example: "This is a reference story."
  }),
  created_at: z10.coerce.date().default(/* @__PURE__ */ new Date()).meta({
    title: "Created At",
    description: "Timestamp when the reference story was created",
    example: "2023-01-01T00:00:00Z"
  }),
  updated_at: z10.coerce.date().default(/* @__PURE__ */ new Date()).meta({
    title: "Updated At",
    description: "Timestamp when the reference story was last updated",
    example: "2023-01-01T00:00:00Z"
  })
}).meta({
  title: "Team Reference Story",
  description: "Schema for stories referenced by a team"
});
var TeamReferenceStorySchema = TeamReferenceStorySchemaInner;
var CreateTeamReferenceStorySchema = TeamReferenceStorySchemaInner.omit({
  id: true,
  created_at: true,
  updated_at: true
});
var UpdateTeamReferenceStorySchema = TeamReferenceStorySchemaInner.pick({
  story: true,
  estimate: true,
  description: true
});
var DeleteTeamReferenceStorySchema = z10.object({
  id: z10.uuid().meta({
    title: "Team Reference Story ID",
    description: "Unique identifier for the team reference story to be deleted",
    example: "team_ref_story_12345"
  })
});
var IntegrationProviderSchemaInner = z10.object({
  id: z10.string().uuid().meta({
    title: "ID",
    description: "Unique identifier for the integration provider",
    example: "123e4567-e89b-12d3-a456-426614174000"
  }),
  name: z10.string().meta({
    title: "Name",
    description: "Name of the integration provider",
    example: "GitHub"
  }),
  description: z10.string().nullable().default(null).meta({
    title: "Description",
    description: "Description of the integration provider",
    example: "Integration with GitHub for issue tracking"
  }),
  created_at: z10.coerce.date().meta({
    title: "Created At",
    description: "Timestamp when the integration provider was created",
    example: "2023-01-01T00:00:00Z"
  }),
  updated_at: z10.coerce.date().meta({
    title: "Updated At",
    description: "Timestamp when the integration provider was last updated",
    example: "2023-01-01T00:00:00Z"
  })
});
var IntegrationProviderSchema = IntegrationProviderSchemaInner;
var CreateIntegrationProviderSchema = IntegrationProviderSchemaInner.omit({
  id: true,
  created_at: true,
  updated_at: true
});
var UpdateIntegrationProviderSchema = IntegrationProviderSchemaInner.pick({
  name: true,
  description: true
}).extend({
  name: z10.string().nullish(),
  description: z10.string().nullable().nullish()
}).partial();
var DeleteIntegrationProviderSchema = z10.object({
  id: z10.string().uuid().meta({
    title: "ID",
    description: "Unique identifier for the integration provider",
    example: "123e4567-e89b-12d3-a456-426614174000"
  })
});
var TeamIntegrationSchemaInner = z10.object({
  id: z10.string().default(`tmint_${createId()}`).meta({
    title: "ID",
    description: "Unique identifier for the specific integration installation for a team",
    example: "tmint_12345"
  }),
  created_at: z10.coerce.date().meta({
    title: "Created At",
    description: "Timestamp when the team was created",
    example: "2023-01-01T00:00:00Z"
  }),
  created_by: z10.uuid().meta({
    title: "Created By",
    description: "Identifier of the user who created the integration",
    example: "user_12345"
  }),
  updated_at: z10.coerce.date().meta({
    title: "Updated At",
    description: "Timestamp when the team was last updated",
    example: "2023-01-01T00:00:00Z"
  }),
  integration_endpoint: z10.string().meta({
    title: "Integration Endpoint",
    description: "The endpoint URL for the integration",
    example: "https://api.integration.com/endpoint"
  }),
  team_id: z10.string().meta({
    title: "Team ID",
    description: "Unique identifier for the team",
    example: "team_12345"
  }),
  integration_id: z10.uuid().meta({
    title: "Integration ID",
    description: "Unique identifier for the integration provider",
    example: "123e4567-e89b-12d3-a456-426614174000"
  }),
  access_token: z10.string().meta({
    title: "Access Token",
    description: "String access token for the integration",
    example: "bXlfc2VjcmV0X2FjY2Vzc190b2tlbg=="
  }),
  configuration_details: z10.json().nullable().default(null).meta({
    title: "Configuration Details",
    description: "Additional configuration details for the integration",
    example: '{ "setting1": "value1", "setting2": "value2" }'
  })
});
var TeamIntegrationSchema = TeamIntegrationSchemaInner;
var CreateTeamIntegrationSchema = TeamIntegrationSchemaInner.omit({
  id: true,
  created_at: true,
  updated_at: true
});
var UpdateTeamIntegrationSchema = TeamIntegrationSchemaInner.pick({
  integration_endpoint: true,
  access_token: true,
  configuration_details: true
}).extend({
  integration_endpoint: z10.string().nullish(),
  access_token: z10.string().nullish(),
  configuration_details: z10.json().nullable().nullish()
}).partial();
var DeleteTeamIntegrationSchema = z10.object({
  id: z10.string().meta({
    title: "ID",
    description: "Unique identifier for the specific integration installation for a team",
    example: "tmint_12345"
  })
});
var WorkspaceSchemaInner = z10.object({
  id: z10.uuid().default(generateUuid).meta({
    title: "Workitem ID",
    description: "Unique identifier for the workitem. A blank value is allowed client-side prior to persistence.",
    example: "123e4567-e89b-12d3-a456-426614174000"
  }),
  created_at: z10.coerce.date().meta({
    title: "Created At",
    description: "Timestamp when the workspace was created",
    example: "2023-01-01T00:00:00Z"
  }),
  updated_at: z10.coerce.date().meta({
    title: "Updated At",
    description: "Timestamp when the workspace was last updated",
    example: "2023-01-01T00:00:00Z"
  }),
  primary_owner_id: z10.uuid().meta({
    title: "Primary Owner ID",
    description: "Unique identifier for the primary owner of the workspace",
    example: "123e4567-e89b-12d3-a456-426614174000"
  }),
  name: z10.string().meta({
    title: "Workspace Name",
    description: "Display name for the workspace",
    example: "My Workspace"
  }),
  slug: z10.string().min(1, "Team slug is required").meta({
    title: "Team Slug",
    description: "URL-friendly identifier for the team",
    example: "my-team"
  }),
  avatar: z10.string().nullable().default(null).meta({
    title: "Avatar URL",
    description: "URL of the team avatar image",
    example: "https://example.com/avatar.png"
  }),
  clerk_org_id: z10.string().nullable().default(null).meta({
    title: "Clerk Organization ID",
    description: "Identifier for the organization in Clerk",
    example: "org_1234567890"
  }),
  stripe_id: z10.string().nullable().default(null).meta({
    title: "Stripe ID",
    description: "Identifier for the Stripe customer",
    example: "cus_1234567890"
  }),
  context: z10.string().nullable().default(null).meta({
    title: "Context",
    description: "Contextual information about the workspace",
    example: "This workspace is for project X."
  }),
  workspace_id: z10.uuid().meta({
    title: "Workspace ID",
    description: "Unique identifier for the workspace",
    example: "123e4567-e89b-12d3-a456-426614174000"
  })
});
var WorkspaceSchema = WorkspaceSchemaInner;
var CreateWorkspaceSchema = WorkspaceSchemaInner.omit({
  id: true,
  created_at: true,
  updated_at: true,
  workspace_id: true
});
var UpdateWorkspaceSchema = WorkspaceSchemaInner.pick({
  slug: true,
  name: true,
  avatar: true,
  context: true
}).extend({
  context: z10.string().nullish(),
  slug: z10.string().min(1, "Team slug is required").nullish(),
  avatar: z10.string().nullish(),
  name: z10.string().nullish()
});

// src/schemas/team.registry.ts
var TeamRegistry = z10.registry();
TeamRegistry.add(TeamSchema);
TeamRegistry.add(CreateTeamSchema);
TeamRegistry.add(UpdateTeamSchema);
TeamRegistry.add(IterationSchema);
TeamRegistry.add(CreateIterationSchema);
TeamRegistry.add(UpdateIterationSchema);
TeamRegistry.add(TeamIterationStatusSchema);
TeamRegistry.add(TeamReferenceStorySchema);
TeamRegistry.add(CreateTeamReferenceStorySchema);
TeamRegistry.add(UpdateTeamReferenceStorySchema);
TeamRegistry.add(DeleteTeamReferenceStorySchema);
var UserSchemaInner = z10.object({
  id: z10.uuid().default(generateUuid).meta({
    title: "User ID",
    description: "The unique identifier for the user",
    example: "550e8400-e29b-41d4-a716-446655440000"
  }),
  email: z10.email("Invalid email address").min(1, "Email is required").meta({
    title: "Email",
    description: "The email address of the user",
    example: "john@email.com"
  }),
  username: z10.string().nullable().meta({
    title: "Username",
    description: "The username of the user",
    example: "johndoe"
  }),
  full_name: z10.string().min(1, "Full name is required").meta({
    title: "Full Name",
    description: "The full name of the user",
    example: "John Doe"
  }),
  avatar_url: z10.url("Avatar URL must be a valid URL").nullable(),
  website: z10.url("Website must be a valid URL").nullable(),
  clerk_id: z10.string().nullable().meta({
    title: "Clerk ID",
    description: "The Clerk authentication ID for the user",
    example: "clerk_12345"
  }),
  stripe_customer_id: z10.string().nullable().meta({
    title: "Stripe Customer ID",
    description: "The Stripe customer ID for billing purposes",
    example: "cus_12345"
  }),
  stripe_subscription_id: z10.string().nullable().meta({
    title: "Stripe Subscription ID",
    description: "The Stripe subscription ID for the user",
    example: "sub_12345"
  }),
  intercom_user: z10.string().nullable().meta({
    title: "Intercom User ID",
    description: "The Intercom user ID for customer support",
    example: "int_12345"
  }),
  global_admin: z10.boolean().default(false).meta({
    title: "Global Admin",
    description: "Indicates if the user has global admin privileges",
    example: false
  }),
  created_at: z10.coerce.date().default(/* @__PURE__ */ new Date()).meta({
    title: "Created At",
    description: "Timestamp when the user was created",
    example: "2023-01-01T00:00:00Z"
  }),
  updated_at: z10.coerce.date().default(/* @__PURE__ */ new Date()).meta({
    title: "Updated At",
    description: "Timestamp when the user was last updated",
    example: "2023-01-01T00:00:00Z"
  })
});
var UserSchema = UserSchemaInner;
var CreateUserSchema = UserSchemaInner.omit({
  id: true,
  created_at: true,
  updated_at: true
});
var UpdateUserSchema = UserSchemaInner.pick({
  email: true,
  username: true,
  full_name: true,
  avatar_url: true,
  website: true,
  stripe_customer_id: true,
  stripe_subscription_id: true
}).partial();
var DeleteUserSchema = z10.object({
  id: z10.uuid().meta({
    title: "User ID",
    description: "The unique identifier for the user",
    example: "550e8400-e29b-41d4-a716-446655440000"
  })
}).meta({ title: "Delete User", description: "Schema for deleting a user" });
var TeamMembershipSchema = z10.object({
  id: z10.uuid().default(generateUuid).meta({
    title: "Membership ID",
    description: "The unique identifier for the team membership",
    example: "550e8400-e29b-41d4-a716-446655440003"
  }),
  user_id: z10.uuid().meta({
    title: "User ID",
    description: "The unique identifier for the user",
    example: "550e8400-e29b-41d4-a716-446655440000"
  }),
  team_id: z10.string().meta({
    title: "Team ID",
    description: "The unique identifier for the team",
    example: "550e8400-e29b-41d4-a716-446655440002"
  }),
  permissions: z10.enum(["owner", "facilitator", "member", "guest", "banned"]).default("member").meta({
    title: "Role",
    description: "The role of the user in the team",
    example: "member"
  }),
  created_at: z10.coerce.date().default(/* @__PURE__ */ new Date()).meta({
    title: "Created At",
    description: "Timestamp when the membership was created",
    example: "2023-01-01T00:00:00Z"
  }),
  updated_at: z10.coerce.date().default(/* @__PURE__ */ new Date()).meta({
    title: "Updated At",
    description: "Timestamp when the membership was last updated",
    example: "2023-01-01T00:00:00Z"
  })
}).meta({
  title: "Team Membership",
  description: "Schema for user membership in a team"
});
var CreateTeamMembershipSchema = TeamMembershipSchema.omit({
  id: true,
  created_at: true,
  updated_at: true
});
var UpdateTeamMembershipSchema = TeamMembershipSchema.pick({
  permissions: true
}).partial();
var DeleteTeamMembershipSchema = z10.object({
  user_id: z10.uuid().meta({
    title: "User ID",
    description: "The unique identifier for the user",
    example: "550e8400-e29b-41d4-a716-446655440000"
  }),
  team_id: z10.string().meta({
    title: "Team ID",
    description: "The unique identifier for the team",
    example: "550e8400-e29b-41d4-a716-446655440002"
  })
}).meta({
  title: "Delete Team Membership",
  description: "Schema for deleting a user membership in a team"
});
var WorkspaceMembershipSchemaInner = z10.object({
  user_id: z10.uuid().meta({
    title: "User ID",
    description: "The unique identifier for the user",
    example: "550e8400-e29b-41d4-a716-446655440000"
  }),
  workspace_id: z10.uuid().meta({
    title: "Workspace ID",
    description: "The unique identifier for the workspace",
    example: "550e8400-e29b-41d4-a716-446655440001"
  }),
  permissions: z10.enum(["owner", "facilitator", "member", "guest", "banned"]).default("member").meta({
    title: "Permissions",
    description: "The permissions level of the user in the workspace",
    example: "member"
  }),
  email: z10.email("Invalid email address").nullable().meta({
    title: "Email",
    description: "The email address of the user",
    example: "john@mail.com"
  }),
  created_at: z10.coerce.date().default(/* @__PURE__ */ new Date()).meta({
    title: "Created At",
    description: "Timestamp when the membership was created",
    example: "2023-01-01T00:00:00Z"
  }),
  updated_at: z10.coerce.date().default(/* @__PURE__ */ new Date()).meta({
    title: "Updated At",
    description: "Timestamp when the membership was last updated",
    example: "2023-01-01T00:00:00Z"
  })
}).meta({
  title: "Workspace Membership",
  description: "Schema for user membership in a workspace"
});
var WorkspaceMembershipSchema = WorkspaceMembershipSchemaInner;
var CreateWorkspaceMembershipSchema = WorkspaceMembershipSchemaInner.omit({
  created_at: true,
  updated_at: true
});
var UpdateWorkspaceMembershipSchema = WorkspaceMembershipSchemaInner.pick({
  permissions: true
}).partial();
var DeleteWorkspaceMembershipSchema = z10.object({
  user_id: z10.uuid().meta({
    title: "User ID",
    description: "The unique identifier for the user",
    example: "550e8400-e29b-41d4-a716-446655440000"
  }),
  workspace_id: z10.uuid().meta({
    title: "Workspace ID",
    description: "The unique identifier for the workspace",
    example: "550e8400-e29b-41d4-a716-446655440001"
  })
}).meta({
  title: "Delete Workspace Membership",
  description: "Schema for deleting a user membership in a workspace"
});
var WorkspaceMemberUserSchema = z10.object({
  id: z10.uuid(),
  workspace_id: z10.uuid(),
  full_name: z10.string().nullable(),
  email: z10.email().nullable(),
  avatar_url: z10.string().nullable(),
  role: z10.enum(["owner", "facilitator", "member", "guest", "banned"]).nullable(),
  admin: z10.boolean(),
  intercom_user: z10.string().nullable(),
  stripe_customer_id: z10.string().nullable(),
  stripe_subscription_id: z10.string().nullable(),
  updated_at: z10.coerce.date()
}).meta({
  title: "Workspace Member User",
  description: "Sanitised view of workspace membership with user details scoped to the workspace"
});

// src/schemas/user.registry.ts
var UserRegistry = z10.registry();
UserRegistry.add(UserSchema);
UserRegistry.add(CreateUserSchema);
UserRegistry.add(UpdateUserSchema);
UserRegistry.add(DeleteUserSchema);
UserRegistry.add(WorkspaceMembershipSchema);
UserRegistry.add(CreateWorkspaceMembershipSchema);
UserRegistry.add(UpdateWorkspaceMembershipSchema);
UserRegistry.add(DeleteWorkspaceMembershipSchema);
UserRegistry.add(WorkspaceMemberUserSchema);
UserRegistry.add(TeamMembershipSchema);
UserRegistry.add(CreateTeamMembershipSchema);
UserRegistry.add(UpdateTeamMembershipSchema);
UserRegistry.add(DeleteTeamMembershipSchema);
var WorkflowMessageInterviewOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().nullable()
});
var WorkflowMessageInterviewLookupConfigSchema = z.object({
  workitems: z.boolean().optional(),
  boards: z.boolean().optional(),
  documents: z.boolean().optional()
}).nullable();
var WorkflowMessageInterviewStepSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  description: z.string().nullable().optional(),
  kind: z.enum(["dropdown", "lookup", "text_response"]).optional(),
  options: z.array(WorkflowMessageInterviewOptionSchema).nullable().optional(),
  lookupConfig: WorkflowMessageInterviewLookupConfigSchema.optional(),
  placeholder: z.string().nullable().optional(),
  selectionMode: z.enum(["single", "multi"]).nullable().optional()
});
var WorkflowMessageBodyInterviewSchema = z.object({
  type: z.literal("interview"),
  steps: z.array(WorkflowMessageInterviewStepSchema),
  selectionMode: z.enum(["single", "multi"]).nullable().optional()
});
var WorkflowMessageBodyTextSchema = z.object({
  type: z.literal("text"),
  content: z.string()
});
var WorkflowMessageRequestedChangeItemSchema = z.object({
  kind: z.string(),
  target: z.string().nullable().optional(),
  description: z.string()
});
var WorkflowMessageBodyRequestedChangesSchema = z.object({
  type: z.literal("requested_changes"),
  items: z.array(WorkflowMessageRequestedChangeItemSchema)
});
var WorkflowMessageBodyApprovalRequestSchema = z.object({
  type: z.literal("approval_request"),
  toolName: z.string(),
  description: z.string(),
  payload: z.record(z.string(), z.unknown()).nullable().optional()
});
var WorkflowMessageBodySchema = z.discriminatedUnion("type", [
  WorkflowMessageBodyTextSchema,
  WorkflowMessageBodyInterviewSchema,
  WorkflowMessageBodyRequestedChangesSchema,
  WorkflowMessageBodyApprovalRequestSchema
]);
var WORKFLOW_MESSAGE_TYPE_VALUES = ["text", "interview", "requested_changes", "approval_request"];
var TeamTagSchema = z10.object({
  id: z10.uuid("Team tag ID must be a valid UUID").meta({
    title: "Team Tag ID",
    description: "Unique identifier for the team tag",
    example: "123e4567-e89b-12d3-a456-426614174000"
  }),
  team_id: z10.string().min(1, "Team ID is required").meta({
    title: "Team ID",
    description: "The team the tag belongs to",
    example: "team_12345"
  }),
  tag: z10.string().min(1, "Tag value is required").meta({
    title: "Tag Value",
    description: "Display label for the tag",
    example: "frontend"
  }),
  type: z10.string().nullable().default(null).meta({
    title: "Tag Type",
    description: "Optional categorisation for the tag",
    example: "discipline"
  }),
  color: z10.string().nullable().default(null).meta({
    title: "Tag Color",
    description: "Optional hex or token used to colourise the tag",
    example: "#FF5733"
  }),
  context: z10.string().nullable().default(null).meta({
    title: "Tag Context",
    description: "Optional context string describing the source or usage of the tag",
    example: "jira"
  }),
  integration_provider_id: z10.uuid("Integration provider ID must be a valid UUID").nullable().default(null).meta({
    title: "Integration Provider ID",
    description: "Integration provider that sourced this tag",
    example: "123e4567-e89b-12d3-a456-426614174000"
  }),
  external_id: z10.string().nullable().default(null).meta({
    title: "External ID",
    description: "Provider-specific tag identifier",
    example: "bug"
  }),
  created_at: z10.coerce.date().default(() => /* @__PURE__ */ new Date()).meta({
    title: "Created At",
    description: "Timestamp when the tag was created",
    example: "2023-01-01T00:00:00.000Z"
  }),
  updated_at: z10.coerce.date().default(() => /* @__PURE__ */ new Date()).meta({
    title: "Updated At",
    description: "Timestamp when the tag was last updated",
    example: "2023-01-02T00:00:00.000Z"
  }),
  workspace_id: z10.uuid("Workspace ID must be a valid UUID").nullable().default(null).meta({
    title: "Workspace ID",
    description: "Workspace associated with the tag if applicable",
    example: "123e4567-e89b-12d3-a456-426614174000"
  })
}).meta({
  title: "Team Tag",
  description: "Metadata describing a tag that can be applied to workitems at the team level."
});
var StatusSchema = z10.enum(["Not Ready", "Ready", "To Do", "Doing", "Done", "Closed", "Removed"]).meta({
  title: "Status",
  description: "The base status of the workitem",
  example: "In Progress"
});
var MoscowPrioritySchema = z10.enum(["Must Have", "Should Have", "Could Have", "Will Not Have"]).meta({
  title: "MoSCoW Priority",
  description: "The MoSCoW priority of the workitem",
  example: "Must Have"
});
var WorkitemSchemaInner = z10.object({
  id: z10.uuid().default(generateUuid).meta({
    title: "Workitem ID",
    description: "Unique identifier for the workitem. A blank value is allowed client-side prior to persistence.",
    example: "123e4567-e89b-12d3-a456-426614174000"
  }),
  team_id: z10.string().min(1, "Team ID is required").nullable().default(null).meta({
    title: "Team ID",
    description: "The ID of the team the workitem belongs to",
    example: "team_12345"
  }),
  current_iteration: z10.string().nullable().default(null).meta({
    title: "Current Iteration",
    description: "The current iteration of the workitem",
    example: "iteration_1"
  }),
  status: z10.string().min(1, "Status is required").default("Not Ready").meta({
    title: "Status",
    description: "The status of the workitem",
    example: "In Progress"
  }),
  title: z10.string().min(1, "Title is required").meta({
    title: "Title",
    description: "The title of the workitem",
    example: "Implement authentication"
  }),
  description: z10.string().nullable().default(null).meta({
    title: "Description",
    description: "The description of the workitem",
    example: "Implement authentication for the application"
  }),
  acceptance_criteria: z10.string().nullable().default(null).meta({
    title: "Acceptance Criteria",
    description: "The acceptance criteria for the workitem",
    example: "User can log in and log out"
  }),
  business_value: z10.number().min(0, "Business value must be 0 or more").nullable().default(null).meta({
    title: "Business Value",
    description: "The business value of the workitem",
    example: 100
  }),
  effort: z10.number().min(0, "Effort must be 0 or more").nullable().default(null).meta({
    title: "Effort",
    description: "The effort required to complete the workitem",
    example: 5
  }),
  activated_date: z10.date().nullable().default(null).meta({
    title: "Activated Date",
    description: "The date the workitem was activated",
    example: "2023-01-01T00:00:00.000Z"
  }),
  closed_date: z10.coerce.date().nullable().default(null).meta({
    title: "Closed Date",
    description: "The date the workitem was closed",
    example: "2023-01-10T00:00:00.000Z"
  }),
  moscow: MoscowPrioritySchema.nullable().default(null).meta({
    title: "MoSCoW",
    description: "The MoSCoW priority of the workitem",
    example: "Must Have"
  }),
  integration_id: z10.string().nullable().default(null).meta({
    title: "Integration ID",
    description: "The ID of the integration the workitem is part of",
    example: "integration_12345"
  }),
  external_system_id: z10.coerce.string().nullable().default(null).meta({
    title: "External System ID",
    description: "The ID of the external system the workitem is linked to",
    example: "ext_12345"
  }),
  iteration_path: z10.string().nullable().default(null).meta({
    title: "Iteration Path",
    description: "The path of the iteration the workitem is part of",
    example: "iteration_1"
  }),
  last_synced_date: z10.coerce.date().nullable().default(null).meta({
    title: "Last Synced Date",
    description: "The date the workitem was last synced",
    example: "2023-01-01T00:00:00.000Z"
  }),
  priority: z10.number().nullable().default(null).meta({
    title: "Priority",
    description: "The priority of the workitem",
    example: 1
  }),
  is_deleted: z10.boolean().default(false).meta({
    title: "Is Deleted",
    description: "Whether the workitem is deleted",
    example: false
  }),
  created_at: z10.coerce.date().default(() => /* @__PURE__ */ new Date()).meta({
    title: "Created At",
    description: "The date the workitem was created",
    example: "2023-01-01T00:00:00.000Z"
  }),
  updated_at: z10.coerce.date().default(() => /* @__PURE__ */ new Date()).meta({
    title: "Updated At",
    description: "The date the workitem was last updated",
    example: "2023-01-02T00:00:00.000Z"
  }),
  created_by: z10.string().nullable().default(null).meta({
    title: "Created By",
    description: "The user who created the workitem",
    example: "user_12345"
  }),
  updated_by: z10.string().nullable().default(null).meta({
    title: "Updated By",
    description: "The user who last updated the workitem",
    example: "user_12345"
  }),
  workitem_type: z10.string().min(1, "Workitem type is required").default("Story").meta({
    title: "Workitem Type",
    description: "The type of the workitem",
    example: "Bug"
  }),
  parent_workitem: z10.string().nullable().default(null).meta({
    title: "Parent Workitem",
    description: "The ID of the parent workitem if applicable",
    example: "workitem_12345"
  }),
  slug: z10.string().nullable().default(null).meta({
    title: "Slug",
    description: "The slug of the workitem",
    example: "workitem-12345"
  }),
  last_indexed_at: z10.coerce.date().nullable().default(null).meta({
    title: "Last Indexed At",
    description: "The date the workitem was last indexed for search",
    example: "2023-01-01T00:00:00.000Z"
  }),
  workspace_id: z10.string().nullable().default(null).meta({
    title: "Workspace ID",
    description: "The ID of the workspace the workitem belongs to",
    example: "123e4567-e89b-12d3-a456-426614174000"
  })
}).meta({
  title: "Workitem",
  description: "A workitem represents a task, feature, bug, or any unit of work that needs to be tracked and managed within a team or project."
});
var WorkitemSchema = WorkitemSchemaInner;
var CreateWorkitemPayloadSchema = WorkitemSchemaInner.omit({
  id: true,
  created_at: true,
  updated_at: true,
  created_by: true,
  updated_by: true,
  last_indexed_at: true
}).partial().meta({
  title: "Create Workitem Payload",
  description: "Schema for validating workitem creation payloads"
});
var UpdateWorkitemPayloadSchema = WorkitemSchemaInner.pick({
  title: true,
  description: true,
  acceptance_criteria: true,
  status: true,
  business_value: true,
  activated_date: true,
  closed_date: true,
  moscow: true,
  integration_id: true,
  external_system_id: true,
  iteration_path: true,
  priority: true,
  is_deleted: true,
  workitem_type: true,
  parent_workitem: true,
  effort: true,
  current_iteration: true
}).partial().extend({
  description: z10.string().nullish(),
  acceptance_criteria: z10.string().nullish(),
  moscow: MoscowPrioritySchema.nullish(),
  priority: z10.number().nullish(),
  business_value: z10.number().nullish(),
  is_deleted: z10.boolean().nullish(),
  workitem_type: z10.string().nullish(),
  parent_workitem: z10.string().nullish(),
  effort: z10.number().nullish(),
  current_iteration: z10.string().nullish(),
  activated_date: z10.coerce.date().nullish(),
  closed_date: z10.coerce.date().nullish(),
  integration_id: z10.string().nullish(),
  external_system_id: z10.string().nullish(),
  iteration_path: z10.string().nullish()
}).meta({
  title: "Update Workitem Payload",
  description: "Schema for validating workitem update payloads"
});
var WorkitemStatus = StatusSchema.enum;
var WorkitemTagSchema = z10.object({
  eid: z10.uuid().default(generateUuid).meta({
    title: "Workitem Tag EID",
    description: "Unique entity identifier for the workitem tag relationship",
    example: "123e4567-e89b-12d3-a456-426614174000"
  }),
  tag_id: z10.uuid("Tag ID must be a valid UUID").meta({
    title: "Tag ID",
    description: "Identifier of the team tag applied to the workitem",
    example: "123e4567-e89b-12d3-a456-426614174000"
  }),
  workitem_id: z10.uuid("Workitem ID must be a valid UUID").meta({
    title: "Workitem ID",
    description: "Identifier of the linked workitem",
    example: "123e4567-e89b-12d3-a456-426614174000"
  }),
  team_id: z10.string().min(1, "Team ID is required").meta({
    title: "Team ID",
    description: "Team that owns the tag relationship",
    example: "team_12345"
  }),
  workspace_id: z10.uuid("Workspace ID must be a valid UUID").nullable().default(null).meta({
    title: "Workspace ID",
    description: "Workspace context for the tag relationship",
    example: "123e4567-e89b-12d3-a456-426614174000"
  })
}).meta({
  title: "Workitem Tag",
  description: "Join record linking a workitem to a team tag."
});
var WorkitemEditMode = /* @__PURE__ */ ((WorkitemEditMode2) => {
  WorkitemEditMode2["Create"] = "create";
  WorkitemEditMode2["EditWorkitem"] = "editWorkitem";
  WorkitemEditMode2["EditCard"] = "editCard";
  return WorkitemEditMode2;
})(WorkitemEditMode || {});
var WorkitemFormSchema = z10.object({
  title: z10.string().min(1, "Title is required").optional(),
  description: z10.string().nullable().default(null),
  acceptance_criteria: z10.string().nullable().default(null),
  effort: z10.number().min(0).nullable().default(null),
  business_value: z10.number().min(0).nullable().default(null),
  status: StatusSchema.default("Not Ready"),
  current_iteration: z10.uuid().nullable().default(null),
  workitem_type: z10.string().min(1, "Workitem type is required").default("Story"),
  moscow: MoscowPrioritySchema.nullable().default(null)
});

// src/schemas/workitem.registry.ts
var WorkitemRegistry = z10.registry();
WorkitemRegistry.add(WorkitemSchema);
WorkitemRegistry.add(StatusSchema);
WorkitemRegistry.add(TeamTagSchema);
WorkitemRegistry.add(WorkitemTagSchema);
WorkitemRegistry.add(CreateWorkitemPayloadSchema);

// src/workitem/mutableFields.ts
var MUTABLE_WORKITEM_FIELD_KEYS = [
  "title",
  "description",
  "acceptance_criteria",
  "business_value",
  "effort",
  "status",
  "priority",
  "moscow",
  "workitem_type",
  "current_iteration",
  "parent_workitem",
  "iteration_path",
  "external_system_id",
  "activated_date",
  "closed_date"
];

export { AriaQuestionSchema, AttachKanbanBoardItemSchema, BoardIntegrationConflictResolutionModeSchema, BoardIntegrationDirectionSchema, BoardIntegrationFieldMappingModeSchema, BoardIntegrationFieldMappingSchema, BoardIntegrationFieldMappingTargetSchema, BoardIntegrationFieldValueMappingSchema, BoardIntegrationMapBoardValueSchema, BoardIntegrationMapEntrySchema, BoardIntegrationMapFiltersSchema, BoardIntegrationMapProviderMappingRefSchema, BoardIntegrationMapProviderRefSchema, BoardIntegrationMapProviderValueSchema, BoardIntegrationMapSchema, BoardIntegrationMapSyncStrategySchema, BoardIntegrationMapTransformSchema, BoardIntegrationProviderKeySchema, BoardIntegrationRuleSchema, BoardIntegrationSavedImportPayloadSchema, BoardValueFieldSchema, BoardValueScopeSchema, BoardWorkitemSchema, CreateBoardWorkitemPayloadSchema, CreateHarmonyPackageDocumentPayloadSchema, CreateHarmonyWorkitemPayloadSchema, CreateIntegrationProviderSchema, CreateIterationSchema, CreateKanbanBoardItemSchema, CreateKanbanColumnSchema, CreateTeamIntegrationSchema, CreateTeamMembershipSchema, CreateTeamReferenceStorySchema, CreateTeamSchema, CreateUserSchema, CreateWorkitemPayloadSchema, CreateWorkspaceMembershipSchema, CreateWorkspaceSchema, DecimalString, DeleteBoardWorkitemPayloadSchema, DeleteHarmonyPackageDocumentPayloadSchema, DeleteHarmonyWorkitemPayloadSchema, DeleteIntegrationProviderSchema, DeleteKanbanBoardItemSchema, DeleteKanbanColumnSchema, DeleteTeamIntegrationSchema, DeleteTeamMembershipSchema, DeleteTeamReferenceStorySchema, DeleteUserSchema, DeleteWorkspaceMembershipSchema, ForecastType, HarmonyPackageBoardSchema, HarmonyPackageDocumentSchema, HarmonyPackageFolderSchema, HarmonyPackageRegistry, HarmonyPackageSchema, HarmonyWorkitemSchema, IntegrationProviderSchema, IterationSchema, KanbanBoardWorkitemInsertSchema, KanbanContextSchema, KanbanExistingHarmonyItemSchema, KanbanSchemaRegistry, KanbanWorkitemMutationSchema, MUTABLE_WORKITEM_FIELD_KEYS, MoscowPriority, MoscowPrioritySchema, PublishStatusSchema, StatusSchema, TeamIntegrationSchema, TeamIterationStatusSchema, TeamMembershipSchema, TeamReferenceStorySchema, TeamRegistry, TeamSchema, TeamTagSchema, UpdateBoardWorkitemPayloadSchema, UpdateHarmonyPackageDocumentPayloadSchema, UpdateHarmonyWorkitemPayloadSchema, UpdateIntegrationProviderSchema, UpdateIterationSchema, UpdateKanbanBoardItemSchema, UpdateKanbanBoardSchema, UpdateKanbanColumnSchema, UpdateKanbanColumnSchemaBase, UpdateTeamIntegrationSchema, UpdateTeamMembershipSchema, UpdateTeamReferenceStorySchema, UpdateTeamSchema, UpdateUserSchema, UpdateWorkitemPayloadSchema, UpdateWorkspaceMembershipSchema, UpdateWorkspaceSchema, UserRegistry, UserSchema, WORKFLOW_MESSAGE_TYPE_VALUES, WorkflowMessageBodyApprovalRequestSchema, WorkflowMessageBodyInterviewSchema, WorkflowMessageBodyRequestedChangesSchema, WorkflowMessageBodySchema, WorkflowMessageBodyTextSchema, WorkflowMessageInterviewLookupConfigSchema, WorkflowMessageInterviewOptionSchema, WorkflowMessageInterviewStepSchema, WorkflowMessageRequestedChangeItemSchema, WorkitemEditMode, WorkitemFormSchema, WorkitemSchema, WorkitemStatus, WorkitemStatusKeySchema, WorkitemStatus as WorkitemStatusSchema, WorkitemTagSchema, WorkspaceMemberUserSchema, WorkspaceMembershipSchema, WorkspaceSchema, generateUuid, resolveEndpointsForHost, useEndpoint };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map